// © 2025–2026 Avv. Dr. Gian Marco Solas · All rights reserved
// Corporate Disclosure Scanner · corporate-scanner.com
// Theory of Interrelation · Legal-physics framework
// Published: JLMI 1/25 (DOI 10.13135/2785-7867/12266)
// Pre-print: "Law is Love" — SSRN 5694423 (physics-reviewed)
// Book: De Lege et Amore (ISBN 9791221030631)
// Unauthorised reproduction, adaptation or commercial use is prohibited.
import { useState, useRef, useCallback, Component } from 'react';
import { callAPI, runAnalysis, runSingleCall, storeScan, buildScanEntry } from './api';
import { fetchShareData, calcLambdaDivergence, fmtCap, fmtPrice } from './sharePrice';
import Methodology from './Methodology';
import { generateReportHTML } from './reportHTML';
import Privacy from './Privacy';

// ─── Labels ───────────────────────────────────────────────────────────────────
const T = {
  appTitle: "Corporate Disclosure Scanner",
  appSub: "Governance & compliance anomaly detection · Listed & public companies · Global markets",
  modeManual: "✍ Manual",
  modeCrawl: "🌐 Scan filings",
  tabText: "Paste text",
  tabPdf: "Upload PDF",
  labelLoad: "Load document",
  labelMeta: "Company metadata",
  labelCompany: "Company / Issuer",
  labelDocType: "Document type",
  labelDocNum: "Filing reference / ISIN",
  labelDate: "Date",
  labelJuris: "Jurisdiction / Exchange",
  labelLang: "Document language",
  placeholderText: "Paste the full text of the corporate disclosure here…\n\nSupported: Annual reports (10-K, 20-F), SEC/ESMA filings, prospectuses, ESG reports, earnings releases, proxy statements, material fact disclosures…",
  placeholderCompany: "e.g. Apple Inc / ISIN: US0378331005",
  placeholderNum: "e.g. SEC CIK 0000320193 / LEI: 549300...",
  btnScan: "Run compliance analysis",
  btnScanning: "Analysing…",
  crawlLabel: "Company name or IR page URL",
  crawlPlaceholder: "e.g. Apple, Tesla, Volkswagen, Enel — or paste an IR/EDGAR URL",
  crawlTip: "Enter a listed company name or URL. The scanner will identify recent filings and analyse each one for governance and compliance anomalies.",
  crawlBtn: "Scan",
  crawlStop: "⛔ Stop",
  logLabel: "Scan log",
  docsLabel: "Documents analysed",
  summaryLabel: "Scan summary",
  backBtn: "← Back to summary",
  resultTitle: "Compliance analysis",
  anomaliesLabel: "Anomalies detected",
  conformityLabel: "Positive findings",
  recommendLabel: "Recommendations",
  chainLabel: "Accountability chain",
  signatory: "Signatory / Certifier",
  internalCtrl: "Internal control / Audit",
  externalCtrl: "Regulator / External auditor",
  noAnomalies: "✓ No anomalies detected",
  highRisk: "High-risk documents",
  totalAnom: "Total anomalies",
  avgRisk: "Avg risk",
  topAnomalies: "Highest-severity anomalies (all documents)",
  clickHint: "← Click a document for full analysis",
  dropPdf: "Drop PDF or click to browse",
  dropSub: "Annual reports, filings, prospectuses, ESG reports",
  risk: "Risk index",
  entropy: "Entropy 🔴",
  inertia: "Inertia 🟡",
  motivePower: "Value creation ⚡",
  entropyLabel: "Value destroyed / misallocated",
  inertiaLabel: "Governance ossification",
  motivePowerLabel: "Genuine shareholder value",
  entropyAnalysis: "Entropy analysis",
  inertiaAnalysis: "Inertia analysis",
  physicsTitle: "Corporate thermodynamics",
  physicsSub: "Entropy · Inertia · Value Creation",
  provision: "Excerpt",
  profile: "Analysis",
  normsViolated: "Standards / rules violated",
  consequence: "Consequence",
  chainLabel: "Accountability chain",
  docTypes: ["Annual Report (10-K / 20-F)","Half-Year / Quarterly Report","Prospectus / IPO","Rights Issue Prospectus","Material Fact / Ad hoc Disclosure","Proxy Statement / AGM Notice","ESG / Sustainability Report","Director's Report","Auditor's Report","Corporate Governance Report","Earnings Release / Press Release","Insider Trading Disclosure","Share Buyback Notice","Related-Party Transaction Disclosure","Regulatory Filing","Other"],
  jurisLevels: ["EU — ESMA / MiFID II / MAR","US — SEC / SOX / Dodd-Frank","UK — FCA / Companies Act 2006","Global — IOSCO / IFRS","LSE / Euronext / NYSE / NASDAQ","Other exchange or OTC"],
  docLangs: ["English","Italiano","Français","Deutsch","Español","Português","Other"],
};

// ─── Anomaly categories ───────────────────────────────────────────────────────
const CAT_META = {
  "GOVERNANCE":       { color:"#7b3fa0", bg:"#f5eefb", icon:"🏛", label:"GOVERNANCE" },
  "FINANCIAL FRAUD":  { color:"#c0392b", bg:"#fdf0ee", icon:"💰", label:"FINANCIAL FRAUD" },
  "MARKET ABUSE":     { color:"#6d1a1a", bg:"#fdf0f0", icon:"📈", label:"MARKET ABUSE" },
  "ESG MISREPORTING": { color:"#2d6a2d", bg:"#eef5ee", icon:"🌿", label:"ESG MISREPORTING" },
  "OPACITY":          { color:"#1a4e8a", bg:"#eaf1fb", icon:"🔒", label:"OPACITY" },
  "RELATED PARTY":    { color:"#b7680e", bg:"#fdf6e7", icon:"🤝", label:"RELATED PARTY" },
  "INSIDER DEALING":  { color:"#880e4f", bg:"#fce4ec", icon:"🕵", label:"INSIDER DEALING" },
  "AUDITOR FAILURE":  { color:"#5d4037", bg:"#fdf3ef", icon:"📋", label:"AUDITOR FAILURE" },
  "COMPETITION LAW":  { color:"#0e6e6e", bg:"#e6f7f7", icon:"⚔", label:"COMPETITION LAW" },
  "DATA PROTECTION":  { color:"#0e4e8a", bg:"#e6f0fb", icon:"🔐", label:"DATA PROTECTION" },
  "PRIVATE CLAIMS":   { color:"#be123c", bg:"#fff1f2", icon:"⚖", label:"PRIVATE CLAIMS" },
};

// ─── Prompt ───────────────────────────────────────────────────────────────────
function buildPrompt(juris) {
  return `You are a senior corporate governance expert, forensic accountant, and securities law analyst covering EU (ESMA, MiFID II, MAR, CSRD), US (SEC, SOX, Dodd-Frank), UK (FCA, Companies Act 2006), and IOSCO/IFRS global standards. You also apply the legal physics framework: human law has thermodynamic properties (entropy = unexpressed/wasted value, inertia = governance ossification, motive_power = productive legal-economic work via Carnot's useful effect). Legal services operate as PC→λC→λS cycles with double erga omnes effect. Assets have spatio-temporal and biological value beyond bi-dimensional price.

Analyse the provided corporate disclosure and return ONLY a single valid JSON object. No prose, no markdown fences, no backticks — raw JSON only, starting with { and ending with }.

Jurisdiction: ${juris || "EU / US / UK / Global"}.

ANOMALY CATEGORIES:
- GOVERNANCE: board independence, CEO/chair duality, remuneration vs performance, entrenchment, committee independence. [INERTIA-heavy]
- FINANCIAL FRAUD: revenue recognition (ASC 606/IFRS 15), off-balance-sheet, non-GAAP misuse, Beneish indicators, undisclosed contingencies, going-concern omissions, bi-dimensional accounting hiding negative externalities. [ENTROPY-heavy]
- MARKET ABUSE: late inside-information (MAR Art.17/SEC 10b-5), selective disclosure (Reg FD), PDMR failures. [ENTROPY-heavy]
- ESG MISREPORTING: greenwashing, missing Scope 3, TCFD/CSRD/SFDR gaps, failure to codify natural capital or biological asset value, SDG misalignment. [ENTROPY-heavy]
- OPACITY: boilerplate MD&A, missing risk factors, absent segment/geographic breakdown (bi-dimensional reporting), undisclosed legal weight (regulatory compliance costs absent from balance sheet), missing spatio-temporal asset valuation. [ENTROPY+INERTIA]
- RELATED PARTY: IAS 24 / S-K Item 404 failures, non-arm's-length, undisclosed SPV/shell. [ENTROPY-heavy]
- INSIDER DEALING: PDMR closed-period trading, option grant timing, absent insider lists. [ENTROPY-heavy]
- AUDITOR FAILURE: long tenure, excess non-audit fees, missing key audit matters (ISA 701). [INERTIA-heavy]
- COMPETITION LAW: undisclosed antitrust investigations or infringement decisions (TFEU Art.101-102, Sherman Act, Chapter I/II CA98), failure to disclose cartel participation or leniency applications, abuse of dominant position indicators (predatory pricing, margin squeeze, tying, refusal to deal), undisclosed merger control proceedings or gun-jumping, bid-rigging or price-fixing exposures (Dir.2014/24 Art.57(4)(d)), state aid distorting competition (TFEU Art.107), failure to quantify competition litigation liability in financial statements, market-sharing or customer-allocation agreements, excessive pricing in regulated sectors. PRIVATE ENFORCEMENT: failure to disclose follow-on damages claims (EU Damages Directive 2014/104/EU), undisclosed pass-on defence assessments, absence of provisions for private antitrust litigation exposure, failure to disclose collective redress proceedings (EU Representative Actions Directive 2020/1828), undisclosed leniency applications triggering private claims, failure to quantify NCA infringement decisions with known damages multipliers, truck cartel and other settled infringements not reflected in contingent liabilities. STANDARDS: TFEU Art.101-102, Reg.1/2003, Sherman Act §1-2, Clayton Act, UK Competition Act 1998, EU Damages Directive 2014/104/EU, EU Representative Actions Directive 2020/1828, DMA Reg.2022/1925. [ENTROPY+INERTIA]
- PRIVATE CLAIMS EXPOSURE (cross-category): flag any disclosure gap that could give rise to private litigation — shareholder claims for misleading statements (MAR Art.17, Prospectus Regulation), product liability exposure not quantified, environmental damage claims (Environmental Liability Directive 2004/35/EC), data breach private claims (GDPR Art.82 individual compensation rights), consumer collective redress (Dir.2020/1828), ESG misrepresentation claims, supply chain liability (CSDDD Dir.2024/1760), human rights litigation exposure, undisclosed class actions or group litigation orders, failure to provision for known litigation risks under IAS 37. NOTE: the erga omnes effect of private claims is systemic — one successful claim sets precedent for thousands. Signal even where exposure is indirect or potential. [HIGH ENTROPY — unexpressed liability suppressing real asset value]
- DATA PROTECTION: missing or inadequate privacy notices (GDPR Art.13-14), absence of DPO appointment where required (Art.37), undisclosed personal data breaches (Art.33-34), unlawful cross-border data transfers without SCCs or adequacy decision (Art.46), missing DPIA for high-risk processing (Art.35), cookie consent failures (ePrivacy Directive), undisclosed DPA enforcement actions or fines, inadequate data retention policies, failure to disclose material data protection risks in annual reports, CCPA non-compliance for US-listed companies. [ENTROPY+INERTIA — regulatory exposure and reputational risk]

ADDITIONAL PHYSICS-BASED METRICS to score 0-100:
- legal_weight_score: regulatory/compliance burden visible in disclosure — high = heavy unquantified legal weight suffocating the entity (the PC/PS=λCF friction problem)
- investability_score: π=λ(R−C) signal — how well the disclosure represents genuine codified asset value, sustainability alignment, and real return potential beyond commoditized price; high = genuinely investable under sustainable criteria
- pressure_source: what triggered this disclosure — "client_pressure" (PC, market/investor demand), "state_pressure" (PS, regulatory obligation), or "voluntary" 
- erga_omnes_effect: 1 sentence — what is the market-wide (erga omnes) consequence of this disclosure beyond the issuer itself

Return ONLY this exact JSON — keep ALL strings SHORT (1 sentence max), max 3 anomalies:
{"overall_verdict":"CRITICAL|SIGNIFICANT ANOMALIES|MINOR IRREGULARITIES|COMPLIANT","risk_score":0-100,"entropy_score":0-100,"inertia_score":0-100,"motive_power":0-100,"legal_weight_score":0-100,"investability_score":0-100,"pressure_source":"state_pressure|client_pressure|voluntary","erga_omnes_effect":"1 sentence","entropy_analysis":"1 sentence","inertia_analysis":"1 sentence","summary":"1-2 sentences","applicable_standards":["string"],"anomalies":[{"title":"≤6 words","category":"GOVERNANCE|FINANCIAL FRAUD|MARKET ABUSE|ESG MISREPORTING|OPACITY|RELATED PARTY|INSIDER DEALING|AUDITOR FAILURE|COMPETITION LAW|DATA PROTECTION|PRIVATE CLAIMS","severity":"high|medium|low","entropy_contribution":0-100,"inertia_contribution":0-100,"standard_violated":"string","doc_section":"string","excerpt":"≤20 words","legal_analysis":"1 sentence","norms_violated":["string"],"consequence":"1 sentence","erga_omnes":"1 sentence","responsibility":{"signatory":{"role":"string","name":null,"liability_type":"string","description":"1 sentence"},"internal_controller":{"roles":["string"],"legal_obligation":"string","description":"1 sentence"},"external_oversight":{"bodies":["string"],"action":"string","deadline":"string","description":"1 sentence"}}}],"positive_points":["string"],"recommendations":"1-2 sentences"}

CRITICAL: never output 0 for scores. Keep every string to 1 sentence. Start with { and do not stop until the JSON is complete and valid.`;
}

const DISCOVERY_PROMPT = `You are a corporate filings expert. Given a listed company name, ticker, and exchange, identify up to 3 real recent public disclosure documents filed by this company. Only include documents you are confident actually exist — do not invent or hallucinate URLs or documents.

Return ONLY valid JSON, nothing else:
{"acts":[{"title":"document title","url":"https://real-url.com","type":"annual_report|half_year|quarterly|prospectus|esg_report|proxy|press_release","date":"YYYY-MM-DD","reference":"ref or null"}]}

If you cannot identify any real filings with confidence, return: {"acts":[]}`;

// ─── Helpers ──────────────────────────────────────────────────────────────────
function riskColor(s) { return s >= 70 ? "#ef4444" : s >= 40 ? "#f59e0b" : "#10b981"; }
function vColor(v) {
  if (!v) return "#10b981";
  const u = v.toUpperCase();
  if (u.includes("CRITICAL")) return "#ef4444";
  if (u.includes("SIGNIFICANT")) return "#f59e0b";
  if (u.includes("MINOR")) return "#f59e0b";
  return "#10b981";
}
function vBg(v) { const c = vColor(v); return c === "#ef4444" ? "rgba(239,68,68,0.1)" : c === "#f59e0b" ? "rgba(245,158,11,0.1)" : "rgba(16,185,129,0.1)"; }

function repairJSON(raw) {
  if (!raw) return null;
  let s = raw.trim()
    .replace(/^```json\s*/i, "").replace(/^```\s*/i, "").replace(/```\s*$/i, "").trim();

  // 1. Try as-is
  try { return JSON.parse(s); } catch {}

  // 2. Find outermost braces
  const start = s.indexOf("{");
  if (start === -1) return null;
  s = s.slice(start);

  // 3. Walk and close open structures
  const stack = [];
  let inStr = false, esc = false, i = 0;
  for (; i < s.length; i++) {
    const c = s[i];
    if (esc) { esc = false; continue; }
    if (c === "\\" && inStr) { esc = true; continue; }
    if (c === '"') { inStr = !inStr; continue; }
    if (inStr) continue;
    if (c === "{") stack.push("}");
    else if (c === "[") stack.push("]");
    else if (c === "}" || c === "]") stack.pop();
  }

  // close open string if needed
  let fixed = inStr ? s + '"' : s;
  // remove trailing comma before closing
  fixed = fixed.replace(/,\s*$/, "");
  // close all open brackets
  fixed = fixed + stack.reverse().join("");

  try { return JSON.parse(fixed); } catch {}

  // 4. Last resort: extract just the top-level scalar fields we can find
  try {
    const extract = (key, isNum) => {
      const m = isNum
        ? new RegExp(`"${key}"\\s*:\\s*(-?\\d+(?:\\.\\d+)?)`).exec(s)
        : new RegExp(`"${key}"\\s*:\\s*"([^"]*?)"`).exec(s);
      return m ? (isNum ? Number(m[1]) : m[1]) : undefined;
    };
    const verdict = extract("overall_verdict");
    const risk    = extract("risk_score", true);
    const summary = extract("summary");
    if (verdict || risk != null) {
      return {
        overall_verdict:     verdict || "SIGNIFICANT ANOMALIES",
        risk_score:          risk ?? null,
        entropy_score:       extract("entropy_score", true)       ?? null,
        inertia_score:       extract("inertia_score", true)       ?? null,
        motive_power:        extract("motive_power", true)        ?? null,
        legal_weight_score:  extract("legal_weight_score", true)  ?? null,
        investability_score: extract("investability_score", true) ?? null,
        entropy_analysis:    extract("entropy_analysis") || "",
        inertia_analysis:    extract("inertia_analysis") || "",
        summary:             summary || "Response was truncated — partial results shown.",
        applicable_standards: [],
        anomalies:           [],
        positive_points:     [],
        recommendations:     "Full analysis unavailable — response was truncated. Please retry.",
      };
    }
  } catch {}

  return null;
}

// callAPI imported from ./api

// ─── Sub-components ───────────────────────────────────────────────────────────
function Spinner({ size = 18 }) {
  return <div style={{ width:size, height:size, flexShrink:0, border:"2px solid rgba(180,200,220,0.2)", borderTopColor:"#60a5fa", borderRadius:"50%", animation:"spin 0.7s linear infinite", display:"inline-block" }}/>;
}

function Lbl({ children }) {
  return <div style={{ fontFamily:"monospace", fontSize:10, letterSpacing:"0.12em", textTransform:"uppercase", color:"#8899aa", marginBottom:10, display:"flex", alignItems:"center", gap:8 }}>{children}<div style={{ flex:1, height:1, background:"rgba(180,200,220,0.15)" }}/></div>;
}

function RiskGauge({ score, label }) {
  const s = Math.max(0, Math.min(100, score || 0));
  const c = riskColor(s), circ = 2 * Math.PI * 28;
  return (
    <div style={{ textAlign:"center" }}>
      <svg width="72" height="72" viewBox="0 0 72 72">
        <circle cx="36" cy="36" r="28" fill="none" stroke="rgba(180,200,220,0.1)" strokeWidth="7"/>
        <circle cx="36" cy="36" r="28" fill="none" stroke={c} strokeWidth="7"
          strokeDasharray={`${(s/100)*circ} ${circ}`} strokeLinecap="round"
          transform="rotate(-90 36 36)" style={{ transition:"stroke-dasharray 1s ease" }}/>
        <text x="36" y="40" textAnchor="middle" fontSize="15" fontWeight="700" fill={c} fontFamily="monospace">{s}</text>
      </svg>
      <div style={{ fontFamily:"monospace", fontSize:8, color:"#8899aa", letterSpacing:"0.06em", textTransform:"uppercase", marginTop:2 }}>{label}</div>
    </div>
  );
}

function PhysicsGauge({ entropyScore, inertiaScore, motivePower }) {
  const circ = 2 * Math.PI * 22;
  const gauges = [
    { score: entropyScore||0,  color:"#ef4444", track:"rgba(239,68,68,0.08)",  label:T.entropy,      sub:T.entropyLabel },
    { score: inertiaScore||0,  color:"#f59e0b", track:"rgba(245,158,11,0.08)", label:T.inertia,       sub:T.inertiaLabel },
    { score: motivePower||0,   color:"#10b981", track:"rgba(16,185,129,0.08)", label:T.motivePower,   sub:T.motivePowerLabel },
  ];
  return (
    <div style={{ border:"1px solid rgba(180,200,220,0.12)", borderRadius:8, background:"rgba(255,255,255,0.03)", padding:"14px 16px", marginBottom:16 }}>
      <div style={{ fontFamily:"monospace", fontSize:9, letterSpacing:"0.12em", textTransform:"uppercase", color:"#8899aa", marginBottom:12, display:"flex", alignItems:"center", gap:8 }}>
        {T.physicsTitle}<div style={{ flex:1, height:1, background:"rgba(180,200,220,0.12)" }}/><span style={{ fontSize:8, opacity:0.7 }}>{T.physicsSub}</span>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12 }}>
        {gauges.map(g => {
          const s = Math.max(0, Math.min(100, g.score));
          return (
            <div key={g.label} style={{ textAlign:"center" }}>
              <svg width="60" height="60" viewBox="0 0 60 60" style={{ display:"block", margin:"0 auto" }}>
                <circle cx="30" cy="30" r="22" fill={g.track} stroke="rgba(180,200,220,0.06)" strokeWidth="1"/>
                <circle cx="30" cy="30" r="22" fill="none" stroke="rgba(180,200,220,0.08)" strokeWidth="5"/>
                <circle cx="30" cy="30" r="22" fill="none" stroke={g.color} strokeWidth="5"
                  strokeDasharray={`${(s/100)*circ} ${circ}`} strokeLinecap="round"
                  transform="rotate(-90 30 30)" style={{ transition:"stroke-dasharray 1.2s ease" }}/>
                <text x="30" y="34" textAnchor="middle" fontSize="13" fontWeight="700" fill={g.color} fontFamily="monospace">{s}</text>
              </svg>
              <div style={{ fontFamily:"monospace", fontSize:9, fontWeight:700, color:g.color, marginTop:5 }}>{g.label}</div>
              <div style={{ fontFamily:"monospace", fontSize:8, color:"#8899aa", marginTop:2, lineHeight:1.4 }}>{g.sub}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AnomalyCard({ a }) {
  const [open, setOpen] = useState(false);
  const sev = a.severity === "high" ? "#ef4444" : a.severity === "medium" ? "#f59e0b" : "#10b981";
  const sevBg = a.severity === "high" ? "rgba(239,68,68,0.08)" : a.severity === "medium" ? "rgba(245,158,11,0.08)" : "rgba(16,185,129,0.08)";
  const cat = CAT_META[a.category] || CAT_META["GOVERNANCE"];
  const resp = a.responsibility;

  return (
    <div style={{ border:"1px solid rgba(180,200,220,0.12)", borderRadius:6, marginBottom:8, background:"rgba(255,255,255,0.03)", overflow:"hidden" }}>
      <div onClick={() => setOpen(!open)} style={{ display:"flex", alignItems:"flex-start", gap:10, padding:"11px 13px", cursor:"pointer", userSelect:"none" }}>
        <div style={{ width:8, height:8, borderRadius:"50%", background:sev, flexShrink:0, marginTop:5, boxShadow:`0 0 6px ${sev}88` }}/>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:3, flexWrap:"wrap" }}>
            <div style={{ fontWeight:700, fontSize:12, color:"#e8f0f8" }}>{a.title}</div>
            <span style={{ fontFamily:"monospace", fontSize:9, padding:"1px 6px", borderRadius:3, background:cat.bg+"22", color:cat.color, border:`1px solid ${cat.color}44`, fontWeight:600, whiteSpace:"nowrap" }}>{cat.icon} {cat.label}</span>
          </div>
          <div style={{ fontFamily:"monospace", fontSize:10, color:"#8899aa", display:"flex", gap:10, flexWrap:"wrap" }}>
            <span style={{ color:sev, fontWeight:700 }}>{(a.severity||"").toUpperCase()}</span>
            {a.doc_section && <span>{a.doc_section}</span>}
            {a.standard_violated && <span style={{ color:"#60a5fa" }}>{a.standard_violated}</span>}
          </div>
          {(a.entropy_contribution != null || a.inertia_contribution != null) && (
            <div style={{ display:"flex", gap:8, marginTop:5 }}>
              {a.entropy_contribution != null && <div style={{ display:"flex", alignItems:"center", gap:4, flex:1 }}>
                <span style={{ fontFamily:"monospace", fontSize:8, color:"#ef4444", whiteSpace:"nowrap" }}>🔴 {a.entropy_contribution}</span>
                <div style={{ flex:1, height:3, background:"rgba(239,68,68,0.1)", borderRadius:2 }}><div style={{ height:"100%", width:`${a.entropy_contribution}%`, background:"#ef4444", borderRadius:2 }}/></div>
              </div>}
              {a.inertia_contribution != null && <div style={{ display:"flex", alignItems:"center", gap:4, flex:1 }}>
                <span style={{ fontFamily:"monospace", fontSize:8, color:"#f59e0b", whiteSpace:"nowrap" }}>🟡 {a.inertia_contribution}</span>
                <div style={{ flex:1, height:3, background:"rgba(245,158,11,0.1)", borderRadius:2 }}><div style={{ height:"100%", width:`${a.inertia_contribution}%`, background:"#f59e0b", borderRadius:2 }}/></div>
              </div>}
            </div>
          )}
        </div>
        <div style={{ fontSize:10, color:"#8899aa", transform:open?"rotate(180deg)":"none", transition:"transform 0.2s", flexShrink:0, marginTop:3 }}>▾</div>
      </div>

      {open && (
        <div style={{ borderTop:"1px solid rgba(180,200,220,0.08)", padding:"12px 13px", background:"rgba(0,0,0,0.2)" }}>
          {a.excerpt && <div style={{ marginBottom:10 }}>
            <div style={{ fontFamily:"monospace", fontSize:9, textTransform:"uppercase", color:"#8899aa", marginBottom:4 }}>{T.provision}</div>
            <div style={{ background:"rgba(255,255,255,0.04)", borderLeft:"3px solid rgba(180,200,220,0.3)", padding:"7px 11px", fontFamily:"monospace", fontSize:11, lineHeight:1.65, color:"#c8d8e8", fontStyle:"italic", borderRadius:"0 4px 4px 0" }}>"{a.excerpt}"</div>
          </div>}
          {a.legal_analysis && <div style={{ marginBottom:10 }}>
            <div style={{ fontFamily:"monospace", fontSize:9, textTransform:"uppercase", color:"#8899aa", marginBottom:4 }}>{T.profile}</div>
            <div style={{ fontSize:12, lineHeight:1.75, color:"#c8d8e8" }}>{a.legal_analysis}</div>
          </div>}
          {(a.norms_violated||[]).length > 0 && <div style={{ marginBottom:10 }}>
            <div style={{ fontFamily:"monospace", fontSize:9, textTransform:"uppercase", color:"#8899aa", marginBottom:4 }}>{T.normsViolated}</div>
            {(a.norms_violated||[]).map((n,i) => <div key={i} style={{ fontFamily:"monospace", fontSize:11, color:"#60a5fa", display:"flex", gap:7, marginBottom:3 }}><span style={{ color:"#8899aa" }}>§</span>{n}</div>)}
          </div>}
          {a.consequence && <div style={{ marginBottom:12 }}>
            <div style={{ fontFamily:"monospace", fontSize:9, textTransform:"uppercase", color:"#8899aa", marginBottom:4 }}>{T.consequence}</div>
            <span style={{ fontFamily:"monospace", fontSize:10, padding:"2px 7px", borderRadius:3, background:sevBg, color:sev, border:`1px solid ${sev}33` }}>{a.consequence}</span>
          </div>}
          {a.erga_omnes && <div style={{ marginBottom:12 }}>
            <div style={{ fontFamily:"monospace", fontSize:9, textTransform:"uppercase", color:"#8899aa", marginBottom:4 }}>⟳ Erga omnes</div>
            <div style={{ fontSize:11, color:"#60a5fa", lineHeight:1.65, fontStyle:"italic" }}>{a.erga_omnes}</div>
          </div>}
          {resp && (
            <div style={{ borderTop:"1px solid rgba(180,200,220,0.08)", paddingTop:12 }}>
              <Lbl>{T.chainLabel}</Lbl>
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {resp.signatory && <div style={{ background:"rgba(239,68,68,0.06)", border:"1px solid rgba(239,68,68,0.2)", borderLeft:"3px solid #ef4444", borderRadius:"0 6px 6px 0", padding:"9px 12px" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:4 }}><span>✍</span><span style={{ fontFamily:"monospace", fontSize:9, fontWeight:700, color:"#ef4444", textTransform:"uppercase" }}>{T.signatory}</span>{resp.signatory.liability_type && <span style={{ fontFamily:"monospace", fontSize:9, padding:"1px 6px", borderRadius:3, background:"rgba(239,68,68,0.1)", color:"#ef4444" }}>{resp.signatory.liability_type}</span>}</div>
                  <div style={{ fontWeight:700, fontSize:12, color:"#e8f0f8", marginBottom:3 }}>{resp.signatory.name && resp.signatory.name!=="null"?`${resp.signatory.name} — `:""}{resp.signatory.role}</div>
                  <div style={{ fontSize:11, color:"#c8d8e8", lineHeight:1.65 }}>{resp.signatory.description}</div>
                </div>}
                {resp.internal_controller && <div style={{ background:"rgba(245,158,11,0.06)", border:"1px solid rgba(245,158,11,0.2)", borderLeft:"3px solid #f59e0b", borderRadius:"0 6px 6px 0", padding:"9px 12px" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:4 }}><span>🏛</span><span style={{ fontFamily:"monospace", fontSize:9, fontWeight:700, color:"#f59e0b", textTransform:"uppercase" }}>{T.internalCtrl}</span></div>
                  <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginBottom:4 }}>{(resp.internal_controller.roles||[]).map((r,i)=><span key={i} style={{ fontFamily:"monospace", fontSize:9, padding:"1px 7px", borderRadius:3, background:"rgba(245,158,11,0.1)", color:"#f59e0b", fontWeight:600 }}>{r}</span>)}</div>
                  {resp.internal_controller.legal_obligation && <div style={{ fontFamily:"monospace", fontSize:10, color:"#60a5fa", marginBottom:3 }}>§ {resp.internal_controller.legal_obligation}</div>}
                  <div style={{ fontSize:11, color:"#c8d8e8", lineHeight:1.65 }}>{resp.internal_controller.description}</div>
                </div>}
                {resp.external_oversight && <div style={{ background:"rgba(96,165,250,0.06)", border:"1px solid rgba(96,165,250,0.2)", borderLeft:"3px solid #60a5fa", borderRadius:"0 6px 6px 0", padding:"9px 12px" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:4 }}><span>⚖</span><span style={{ fontFamily:"monospace", fontSize:9, fontWeight:700, color:"#60a5fa", textTransform:"uppercase" }}>{T.externalCtrl}</span></div>
                  <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginBottom:4 }}>{(resp.external_oversight.bodies||[]).map((o,i)=><span key={i} style={{ fontFamily:"monospace", fontSize:9, padding:"1px 7px", borderRadius:3, background:"rgba(96,165,250,0.1)", color:"#60a5fa", fontWeight:600 }}>{o}</span>)}</div>
                  {resp.external_oversight.action && <div style={{ fontWeight:700, fontSize:11, color:"#e8f0f8", marginBottom:3 }}>→ {resp.external_oversight.action}</div>}
                  {resp.external_oversight.deadline && <div style={{ fontFamily:"monospace", fontSize:10, color:"#ef4444", marginBottom:3 }}>⏱ {resp.external_oversight.deadline}</div>}
                  <div style={{ fontSize:11, color:"#c8d8e8", lineHeight:1.65 }}>{resp.external_oversight.description}</div>
                </div>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Instruct Counsel Button ──────────────────────────────────────────────────
function InstructCounselButton({ result, company, shareData }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const anomalies = result?.anomalies || [];
  if (!result || anomalies.length === 0) return null;

  const highAnomalies = anomalies.filter(a => a.severity === "high");
  const topAnomalies  = (highAnomalies.length > 0 ? highAnomalies : anomalies).slice(0, 3);

  const norms = [...new Set(topAnomalies.flatMap(a => a.norms_violated || [a.standard_violated]).filter(Boolean))].slice(0, 5);
  const cats  = [...new Set(topAnomalies.map(a => a.category))];

  const lambdaNote = shareData?.lambdaSignal?.signal === "OVERVALUED"
    ? `\n\nOur analysis also identifies a significant λ-divergence between the company's governance score and its current market valuation, suggesting the market has not yet priced the disclosed risks. This may be relevant to your assessment of shareholder exposure.`
    : "";

  const email = `Subject: Legal Analysis — ${company || "Corporate Disclosure"} · Transforming Liability into Investment

Dear [Name],

I am writing following a legal-physics analysis of ${company || "your company"}'s recent corporate disclosure under the Corporate Disclosure Scanner framework (corporate-scanner.com), applying the Theory of Interrelation published in the Journal of Law, Market & Innovation (JLMI 1/25).

The analysis identified the following areas of legal concern:

${topAnomalies.map((a, i) => `${i+1}. ${a.title} (${a.category})
   Standard violated: ${a.standard_violated || "—"}
   Assessment: ${a.legal_analysis || a.consequence || "—"}`).join("\n\n")}

Applicable legal standards: ${norms.join(", ") || cats.join(", ")}.

Overall measurement: ${result.overall_verdict} · Risk Index: ${result.risk_score}/100 · Entropy (S=k·logW): ${result.entropy_score}/100 · Legal Weight (PC=λCF): ${result.legal_weight_score||"—"}/100.${lambdaNote}

The purpose of this letter is not only to draw attention to the above compliance gaps, but to explore whether the liabilities and unexpressed value identified in this analysis can be transformed into new investment and value for the company and its stakeholders — applying the geometric settlement model (λS = CC + CD + D) developed in the Theory of Interrelation.

Under this approach, rather than a binary win/lose outcome, the legal-physics framework enables the identification of settlement structures where defendants' liabilities become productive investments: in restructured governance, in remediated assets, in sustainable value chains — aligned to UN SDGs and applicable law.

My practice covers ${cats.includes("COMPETITION LAW") ? "competition law, " : ""}${cats.includes("MARKET ABUSE") ? "securities regulation, " : ""}${cats.includes("GOVERNANCE") ? "corporate governance restructuring, " : ""}${cats.includes("ESG MISREPORTING") ? "ESG regulatory compliance, " : ""}${cats.includes("DATA PROTECTION") ? "data protection, " : ""}and the codification of sustainable legal systems.

I would welcome the opportunity to discuss how the findings above can be converted from liability into investment at your convenience.

Kind regards,
Avv. Dr. Gian Marco Solas
Sustainab-Law
gmsolas@sustainab-law.eu
corporate-scanner.com`;

  const copy = () => {
    navigator.clipboard.writeText(email);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{ marginTop:16 }}>
      <button onClick={() => setOpen(!open)}
        style={{ width:"100%", padding:"12px", background:open?"rgba(96,165,250,0.15)":"rgba(96,165,250,0.08)", color:"#60a5fa", border:"1px solid rgba(96,165,250,0.3)", borderRadius:6, fontFamily:"monospace", fontSize:12, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8, letterSpacing:"0.05em", transition:"all 0.15s" }}>
        ⚖ Instruct Counsel — Transforming Liability into Investment
        <span style={{ fontSize:10, opacity:0.7, transform:open?"rotate(180deg)":"none", transition:"transform 0.2s" }}>▾</span>
      </button>
      <div style={{ fontFamily:"monospace", fontSize:9, color:"#3b4a5a", marginTop:6, lineHeight:1.7, padding:"0 4px" }}>
        The anomalies above represent not just compliance risk but unexpressed value. The geometric settlement model (λS = CC + CD + D) developed in the Theory of Interrelation enables transforming identified liabilities into new investment — for the company, its stakeholders, and the legal system. <a href="https://papers.ssrn.com/sol3/papers.cfm?abstract_id=5694423" target="_blank" rel="noopener noreferrer" style={{ color:"#60a5fa", textDecoration:"none" }}>Law is Love — SSRN 5694423</a> · §3.3 · Unexpressed value in legal systems: <a href="https://www.1love.works" target="_blank" rel="noopener noreferrer" style={{ color:"#a78bfa", textDecoration:"none" }}>1love.works</a>
      </div>

      {open && (
        <div style={{ marginTop:6, border:"1px solid rgba(96,165,250,0.2)", borderRadius:6, background:"rgba(6,10,20,0.8)", overflow:"hidden" }}>
          <div style={{ padding:"10px 14px", borderBottom:"1px solid rgba(96,165,250,0.1)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div style={{ fontFamily:"monospace", fontSize:9, color:"#60a5fa", textTransform:"uppercase", letterSpacing:"0.1em" }}>
              Draft mandate letter — based on scan anomalies · Edit before sending
            </div>
            <button onClick={copy} style={{ padding:"4px 12px", background:copied?"rgba(16,185,129,0.2)":"rgba(96,165,250,0.1)", color:copied?"#10b981":"#60a5fa", border:`1px solid ${copied?"rgba(16,185,129,0.3)":"rgba(96,165,250,0.3)"}`, borderRadius:4, fontFamily:"monospace", fontSize:9, cursor:"pointer" }}>
              {copied ? "✓ Copied" : "Copy"}
            </button>
          </div>
          <div style={{ padding:"14px", fontFamily:"monospace", fontSize:11, color:"#94a3b8", lineHeight:1.8, whiteSpace:"pre-wrap", maxHeight:380, overflowY:"auto" }}>
            {email}
          </div>
          <div style={{ padding:"8px 14px", borderTop:"1px solid rgba(96,165,250,0.1)", fontFamily:"monospace", fontSize:8, color:"#3b4a5a" }}>
            ⚠ This draft is generated from scan results. Review carefully before sending. Post-instruction work is subject to legal professional privilege.
          </div>
        </div>
      )}
    </div>
  );
}

function ResultsPanel({ result, meta, shareData, company }) {
  if (!result) return null;
  const anomalies = result.anomalies || [];
  const high = anomalies.filter(a => a.severity === "high").length;
  const med  = anomalies.filter(a => a.severity === "medium").length;
  const low  = anomalies.filter(a => a.severity === "low").length;
  const byCat = k => anomalies.filter(a => a.category === k).length;
  const score = result.risk_score || 0;
  const verdict = result.overall_verdict || "";
  const vc = vColor(verdict), vb = vBg(verdict);

  const legalWeight   = result.legal_weight_score ?? null;
  const investability = result.investability_score ?? null;
  const pressureSrc   = result.pressure_source || null;
  const ergaOmnes     = result.erga_omnes_effect || null;

  const pressureLabel = pressureSrc === "state_pressure"   ? { label:"PS — State / Regulatory pressure", color:"#7b3fa0", icon:"🏛" }
                      : pressureSrc === "client_pressure"  ? { label:"PC — Market / Investor pressure",  color:"#3b82f6", icon:"📈" }
                      : pressureSrc === "voluntary"        ? { label:"Voluntary disclosure",             color:"#10b981", icon:"✋" }
                      : null;

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:14, paddingBottom:13, borderBottom:"1px solid rgba(180,200,220,0.12)" }}>
        <div>
          <div style={{ fontSize:15, fontWeight:700, color:"#e8f0f8" }}>{T.resultTitle}</div>
          <div style={{ fontFamily:"monospace", fontSize:10, color:"#8899aa", marginTop:2 }}>{meta || "Document"} · {new Date().toLocaleDateString()}</div>
        </div>
        <span style={{ fontFamily:"monospace", fontSize:10, padding:"4px 10px", borderRadius:4, background:vb, color:vc, border:`1px solid ${vc}44`, fontWeight:600 }}>{verdict}</span>
      </div>

      {(result.applicable_standards||[]).length > 0 && (
        <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginBottom:12 }}>
          {result.applicable_standards.map((s,i) => <span key={i} style={{ fontFamily:"monospace", fontSize:9, padding:"2px 8px", borderRadius:3, background:"rgba(96,165,250,0.08)", color:"#60a5fa", border:"1px solid rgba(96,165,250,0.2)" }}>{s}</span>)}
        </div>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"72px 1fr", gap:12, marginBottom:14, alignItems:"center" }}>
        <RiskGauge score={score} label={T.risk}/>
        <div style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(180,200,220,0.1)", borderRadius:6, padding:"10px 13px", fontSize:12, lineHeight:1.75, color:"#c8d8e8" }}>{result.summary}</div>
      </div>

      {(result.entropy_score != null) && <PhysicsGauge entropyScore={result.entropy_score} inertiaScore={result.inertia_score} motivePower={result.motive_power}/>}

      {(result.entropy_analysis || result.inertia_analysis) && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:16 }}>
          {result.entropy_analysis && <div style={{ border:"1px solid rgba(239,68,68,0.2)", borderLeft:"3px solid #ef4444", borderRadius:"0 6px 6px 0", padding:"10px 12px", background:"rgba(239,68,68,0.04)" }}>
            <div style={{ fontFamily:"monospace", fontSize:9, textTransform:"uppercase", color:"#ef4444", marginBottom:6, fontWeight:700 }}>🔴 {T.entropyAnalysis}</div>
            <div style={{ fontSize:11, lineHeight:1.7, color:"#c8d8e8" }}>{result.entropy_analysis}</div>
          </div>}
          {result.inertia_analysis && <div style={{ border:"1px solid rgba(245,158,11,0.2)", borderLeft:"3px solid #f59e0b", borderRadius:"0 6px 6px 0", padding:"10px 12px", background:"rgba(245,158,11,0.04)" }}>
            <div style={{ fontFamily:"monospace", fontSize:9, textTransform:"uppercase", color:"#f59e0b", marginBottom:6, fontWeight:700 }}>🟡 {T.inertiaAnalysis}</div>
            <div style={{ fontSize:11, lineHeight:1.7, color:"#c8d8e8" }}>{result.inertia_analysis}</div>
          </div>}
        </div>
      )}

      {/* Legal Physics: Weight + Investability + Pressure */}
      {(legalWeight != null || investability != null || pressureLabel || ergaOmnes) && (
        <div style={{ border:"1px solid rgba(180,200,220,0.12)", borderRadius:8, background:"rgba(255,255,255,0.03)", padding:"14px 16px", marginBottom:16 }}>
          <div style={{ fontFamily:"monospace", fontSize:9, letterSpacing:"0.12em", textTransform:"uppercase", color:"#8899aa", marginBottom:12, display:"flex", alignItems:"center", gap:8 }}>
            ⚡ Legal Physics
            <div style={{ flex:1, height:1, background:"rgba(180,200,220,0.12)" }}/>
            <span style={{ fontSize:8, opacity:0.7 }}>PC→λC→λS · Carnot motive power · π=λ(R−C)</span>
          </div>

          {/* Weight + Investability bars */}
          {(legalWeight != null || investability != null) && (
            <div className="score-grid-2" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:pressureLabel||ergaOmnes?12:0 }}>
              {legalWeight != null && (
                <div>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                    <span style={{ fontFamily:"monospace", fontSize:9, color:"#ef4444" }}>⚖ Legal Weight (PC=λCF)</span>
                    <span style={{ fontFamily:"monospace", fontSize:9, fontWeight:700, color:"#ef4444" }}>{legalWeight}/100</span>
                  </div>
                  <div style={{ height:6, background:"rgba(239,68,68,0.1)", borderRadius:3 }}>
                    <div style={{ height:"100%", width:`${legalWeight}%`, background: legalWeight>70?"#ef4444":legalWeight>40?"#f59e0b":"#10b981", borderRadius:3, transition:"width 1s ease" }}/>
                  </div>
                  <div style={{ fontFamily:"monospace", fontSize:8, color:"#8899aa", marginTop:3 }}>
                    {legalWeight>70?"Heavy regulatory friction suppressing value":legalWeight>40?"Moderate legal burden":"Low legal weight — lean structure"}
                  </div>
                </div>
              )}
              {investability != null && (
                <div>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                    <span style={{ fontFamily:"monospace", fontSize:9, color:"#10b981" }}>🌱 Investability π=λ(R−C)</span>
                    <span style={{ fontFamily:"monospace", fontSize:9, fontWeight:700, color:"#10b981" }}>{investability}/100</span>
                  </div>
                  <div style={{ height:6, background:"rgba(16,185,129,0.1)", borderRadius:3 }}>
                    <div style={{ height:"100%", width:`${investability}%`, background: investability>60?"#10b981":investability>30?"#f59e0b":"#ef4444", borderRadius:3, transition:"width 1s ease" }}/>
                  </div>
                  <div style={{ fontFamily:"monospace", fontSize:8, color:"#8899aa", marginTop:3 }}>
                    {investability>60?"Genuine codified asset value — sustainable signal":investability>30?"Partial sustainability alignment":"Commoditised — real value not represented"}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Pressure source */}
          {pressureLabel && (
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:ergaOmnes?10:0, padding:"7px 10px", background:`${pressureLabel.color}11`, border:`1px solid ${pressureLabel.color}33`, borderRadius:6 }}>
              <span style={{ fontSize:14 }}>{pressureLabel.icon}</span>
              <div>
                <div style={{ fontFamily:"monospace", fontSize:9, fontWeight:700, color:pressureLabel.color, textTransform:"uppercase", letterSpacing:"0.06em" }}>Disclosure trigger</div>
                <div style={{ fontFamily:"monospace", fontSize:10, color:"#c8d8e8", marginTop:1 }}>{pressureLabel.label}</div>
              </div>
            </div>
          )}

          {/* Erga omnes effect */}
          {ergaOmnes && (
            <div style={{ marginTop:pressureLabel?8:0, padding:"8px 10px", background:"rgba(96,165,250,0.05)", border:"1px solid rgba(96,165,250,0.15)", borderRadius:6, borderLeft:"3px solid #60a5fa" }}>
              <div style={{ fontFamily:"monospace", fontSize:9, color:"#60a5fa", fontWeight:700, textTransform:"uppercase", marginBottom:4 }}>⟳ Erga omnes effect</div>
              <div style={{ fontSize:11, color:"#c8d8e8", lineHeight:1.65 }}>{ergaOmnes}</div>
            </div>
          )}
        </div>
      )}

      <div className="score-grid-4" style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:7, marginBottom:10 }}>
        {[{n:high,l:"High",c:"#ef4444"},{n:med,l:"Med",c:"#f59e0b"},{n:low,l:"Low",c:"#10b981"},{n:anomalies.length,l:"Total",c:"#e8f0f8"}].map(s=>(
          <div key={s.l} style={{ border:"1px solid rgba(180,200,220,0.1)", borderRadius:6, padding:"9px 5px", textAlign:"center", background:"rgba(255,255,255,0.03)" }}>
            <div style={{ fontSize:20, fontWeight:700, color:s.c, lineHeight:1 }}>{s.n}</div>
            <div style={{ fontFamily:"monospace", fontSize:8, color:"#8899aa", marginTop:3, textTransform:"uppercase" }}>{s.l}</div>
          </div>
        ))}
      </div>
      <div className="cat-grid-4" style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:5, marginBottom:7 }}>
        {Object.entries(CAT_META).slice(0,4).map(([k,m]) => (
          <div key={k} style={{ border:`1px solid ${m.color}33`, borderRadius:6, padding:"6px 4px", textAlign:"center", background:m.bg+"11" }}>
            <div style={{ fontSize:14, fontWeight:700, color:m.color }}>{byCat(k)}</div>
            <div style={{ fontFamily:"monospace", fontSize:7, color:m.color, marginTop:2, textTransform:"uppercase", opacity:0.85 }}>{m.icon} {m.label}</div>
          </div>
        ))}
      </div>
      <div className="cat-grid-4" style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:5, marginBottom:16 }}>
        {Object.entries(CAT_META).slice(4).map(([k,m]) => (
          <div key={k} style={{ border:`1px solid ${m.color}33`, borderRadius:6, padding:"6px 4px", textAlign:"center", background:m.bg+"11" }}>
            <div style={{ fontSize:14, fontWeight:700, color:m.color }}>{byCat(k)}</div>
            <div style={{ fontFamily:"monospace", fontSize:7, color:m.color, marginTop:2, textTransform:"uppercase", opacity:0.85 }}>{m.icon} {m.label}</div>
          </div>
        ))}
      </div>

      <Lbl>{T.anomaliesLabel}</Lbl>
      {anomalies.length === 0
        ? <div style={{ padding:18, textAlign:"center", fontFamily:"monospace", fontSize:12, color:"#10b981", background:"rgba(16,185,129,0.06)", borderRadius:6, border:"1px solid rgba(16,185,129,0.2)" }}>{T.noAnomalies}</div>
        : anomalies.map((a,i) => <AnomalyCard key={i} a={a}/>)
      }
      {(result.positive_points||[]).length > 0 && <div style={{ marginTop:16 }}>
        <Lbl>{T.conformityLabel}</Lbl>
        <div style={{ border:"1px solid rgba(180,200,220,0.1)", borderRadius:6, background:"rgba(255,255,255,0.03)", padding:"2px 13px" }}>
          {(result.positive_points||[]).map((p,i) => <div key={i} style={{ fontSize:12, color:"#c8d8e8", padding:"7px 0", borderBottom:i<result.positive_points.length-1?"1px solid rgba(180,200,220,0.08)":"none", display:"flex", gap:9 }}><span style={{ color:"#10b981" }}>✓</span>{p}</div>)}
        </div>
      </div>}
      {result.recommendations && <div style={{ marginTop:16 }}>
        <Lbl>{T.recommendLabel}</Lbl>
        <div style={{ background:"rgba(96,165,250,0.06)", border:"1px solid rgba(96,165,250,0.15)", borderRadius:6, padding:"12px 13px", fontSize:12, lineHeight:1.75, color:"#c8d8e8" }}>{result.recommendations}</div>
      </div>}

      {/* Share Price & λ-Divergence — Beta placeholder */}
      <div style={{ border:"1px solid rgba(96,165,250,0.15)", borderRadius:8, background:"rgba(96,165,250,0.03)", padding:"16px", marginTop:16 }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
          <div style={{ fontFamily:"monospace", fontSize:9, letterSpacing:"0.12em", textTransform:"uppercase", color:"#60a5fa", fontWeight:700 }}>
            📈 Market Data · λ-Divergence Signal
          </div>
          <span style={{ fontFamily:"monospace", fontSize:8, padding:"2px 8px", borderRadius:10, background:"rgba(96,165,250,0.1)", color:"#60a5fa", border:"1px solid rgba(96,165,250,0.2)" }}>Coming in v2</span>
        </div>
        <div style={{ fontSize:11, color:"#64748b", lineHeight:1.8, marginBottom:10 }}>
          The next version of the scanner will integrate live share price data to calculate the <strong style={{ color:"#c8d8e8" }}>λ-divergence signal</strong> — the gap between what the market prices and what the legal-physics framework measures as the company's real governance quality and asset value.
        </div>
        <div style={{ fontSize:11, color:"#64748b", lineHeight:1.8, marginBottom:10 }}>
          A high entropy score combined with a high market valuation indicates the market has not yet priced the governance risk detected in this disclosure. A low investability score against a premium valuation is a structural signal visible in the disclosure before it appears in the price.
        </div>
        <div style={{ padding:"10px 12px", background:"rgba(245,158,11,0.05)", border:"1px solid rgba(245,158,11,0.15)", borderRadius:6, fontSize:11, color:"#94a3b8", lineHeight:1.7 }}>
          <strong style={{ color:"#f59e0b" }}>Note:</strong> The λ-divergence signal is withheld in this beta version. The empirical dataset is in an early stage of development and the correlation between disclosure scores and share price movements has not yet been validated at scale. We do not suggest any investment, short, or trading position on the basis of this analysis. If the scores in this report raise legal or regulatory concerns, please <a href="https://calendly.com/gmsolas-sustainab-law/30min" target="_blank" rel="noopener noreferrer" style={{ color:"#60a5fa", textDecoration:"none" }}>book a consultation</a> or contact your lawyer.
        </div>
      </div>

      {/* Score interpretation */}
      <div style={{ marginTop:16, padding:"14px 16px", background:"rgba(255,255,255,0.02)", border:"1px solid rgba(180,200,220,0.1)", borderRadius:8 }}>
        <div style={{ fontFamily:"monospace", fontSize:9, fontWeight:700, color:"#8899aa", textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:12 }}>📐 How to read these scores</div>

        <div style={{ fontSize:11, color:"#64748b", lineHeight:1.8, marginBottom:12 }}>
          These ranges are provisional thresholds based on the legal-physics framework — not empirically calibrated benchmarks. They will be refined as the dataset grows.
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:12 }} className="score-grid-2">
          {[
            { label:"0 - 39", verdict:"Compliant", color:"#10b981", desc:"Disclosure meets applicable standards. Low anomaly density." },
            { label:"40 - 69", verdict:"Significant Anomalies", color:"#f59e0b", desc:"Material gaps detected. Legal or regulatory attention warranted." },
            { label:"70 - 84", verdict:"High Risk", color:"#ef4444", desc:"Serious disclosure failures. Multiple standard violations identified." },
            { label:"85 - 100", verdict:"Critical", color:"#dc2626", desc:"Systemic failures. Immediate legal assessment strongly advised." },
          ].map(r => (
            <div key={r.label} style={{ padding:"8px 10px", background:r.color+"08", border:"1px solid "+r.color+"22", borderRadius:6 }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                <span style={{ fontFamily:"monospace", fontSize:9, fontWeight:700, color:r.color }}>{r.label}</span>
                <span style={{ fontFamily:"monospace", fontSize:9, color:r.color }}>{r.verdict}</span>
              </div>
              <div style={{ fontSize:10, color:"#64748b", lineHeight:1.5 }}>{r.desc}</div>
            </div>
          ))}
        </div>

        <div style={{ fontSize:11, color:"#64748b", lineHeight:1.8, borderTop:"1px solid rgba(180,200,220,0.08)", paddingTop:10 }}>
          <strong style={{ color:"#94a3b8" }}>On benchmarking:</strong> These scores represent the <strong style={{ color:"#c8d8e8" }}>first empirical application</strong> of the legal-physics framework to corporate disclosures. The formulas (S=k logW, PC/PS=lambdaCF, pi=lambda(R-C), W=MG) have never previously been applied to measure disclosure quality. There is no prior dataset and no historical benchmark because <strong style={{ color:"#c8d8e8" }}>these measurements are being taken for the first time</strong>. Every scan contributes to building the empirical reference dataset. These thresholds are provisional and will be refined as the dataset grows.
        </div>
        <div style={{ fontSize:11, color:"#64748b", lineHeight:1.8, borderTop:"1px solid rgba(180,200,220,0.08)", paddingTop:10, marginTop:10 }}>
          <strong style={{ color:"#94a3b8" }}>Scientific collaboration:</strong> Researchers in statistical physics, econophysics, complexity science, or legal theory interested in testing these findings are welcome to contact <a href="mailto:gmsolas@sustainab-law.eu?subject=Scientific Collaboration - Corporate Disclosure Scanner" style={{ color:"#a78bfa", textDecoration:"none" }}>gmsolas@sustainab-law.eu</a>. Published framework: <a href="https://ojs.unito.it/index.php/JLMI/article/view/12266" target="_blank" rel="noopener noreferrer" style={{ color:"#60a5fa", textDecoration:"none" }}>JLMI 1/25</a>
        </div>
      </div>

      {/* Important disclaimer */}
      <div style={{ marginTop:16, padding:"14px 16px", background:"rgba(245,158,11,0.04)", border:"1px solid rgba(245,158,11,0.2)", borderRadius:8, borderLeft:"3px solid rgba(245,158,11,0.5)" }}>
        <div style={{ fontFamily:"monospace", fontSize:9, fontWeight:700, color:"#f59e0b", textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:8 }}>⚠ Important Notice</div>
        <div style={{ fontSize:11, color:"#94a3b8", lineHeight:1.8 }}>
          The scores and signals produced by this instrument are <strong style={{ color:"#c8d8e8" }}>systematic measurements that aim to be objective</strong> through a legal-physics framework currently under empirical validation. They do <strong style={{ color:"#c8d8e8" }}>not</strong> constitute investment advice, financial advice, or legal advice.
        </div>
        <div style={{ fontSize:11, color:"#94a3b8", lineHeight:1.8, marginTop:8 }}>
          These results should <strong style={{ color:"#c8d8e8" }}>not</strong> be used as the sole basis for investment decisions, short positions, shareholder claims, regulatory complaints, or legal proceedings. The empirical dataset is in an early stage of development and scores may vary between scans of the same document.
        </div>
        <div style={{ fontSize:11, color:"#94a3b8", lineHeight:1.8, marginTop:8 }}>
          <strong style={{ color:"#f59e0b" }}>AI limitation:</strong> This analysis is generated by an AI system and may contain errors, omissions, or hallucinations, particularly at an early stage of development. <strong style={{ color:"#c8d8e8" }}>Human legal review by a qualified lawyer is always required</strong> before drawing any legal or commercial conclusion from these results.
        </div>
        <div style={{ fontFamily:"monospace", fontSize:9, color:"#64748b", marginTop:10, paddingTop:8, borderTop:"1px solid rgba(245,158,11,0.1)" }}>
          Corporate Disclosure Scanner · Demo · Legal-physics framework physics-reviewed, pending empirical publication · corporate-scanner.com
        </div>
      </div>

      <div style={{ height:28 }}/>
    </div>
  );
}

// ─── PDF export ───────────────────────────────────────────────────────────────

function exportPDF(result, meta, company) {
  const html = generateReportHTML(result, meta, company, CAT_META);
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const filename = 'compliance-report-' + (company||meta||'scan').replace(/[^a-z0-9]/gi,'-').toLowerCase() + '.html';
  a.download = filename;
  a.target = '_blank';
  document.body.appendChild(a);
  a.click();
  setTimeout(function(){ document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
}
let SESSION_SCANS = 0;
const BETA_LIMIT = 1;

function ScanLimitBanner({ onRequest }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:400, gap:20, padding:32, textAlign:"center" }}>
      <div style={{ fontSize:36 }}>🔒</div>
      <div style={{ fontFamily:"monospace", fontSize:14, fontWeight:700, color:"#e8f0f8" }}>Demo limit reached</div>
      <div style={{ fontSize:12, color:"#94a3b8", lineHeight:1.8, maxWidth:380 }}>
        The demo version includes <strong style={{ color:"#c8d8e8" }}>1 free scan per session</strong>.<br/>
        Book a legal consultation to discuss how the anomalies detected can be transformed from liability into new investment — applying the geometric settlement model of the Theory of Interrelation (De Lege et Amore).
      </div>
      <button onClick={onRequest}
        style={{ padding:"12px 28px", background:"#3b82f6", color:"white", border:"none", borderRadius:6, fontFamily:"monospace", fontSize:12, fontWeight:700, cursor:"pointer", letterSpacing:"0.05em" }}>
        ⚖ Book Consultation
      </button>
      <div style={{ fontFamily:"monospace", fontSize:10, color:"#3b4a5a" }}>corporate-scanner.com · Demo</div>
    </div>
  );
}

function requestAccess() {
  window.open("https://calendly.com/gmsolas-sustainab-law/legal-consultation-public-entity", "_blank");
}

function ManualMode() {
  const [tab, setTab] = useState("text");
  const [docText, setDocText] = useState("");
  const [pdfBase64, setPdfBase64] = useState("");
  const [fileName, setFileName] = useState("");
  const [company, setCompany] = useState("");
  const [ticker, setTicker] = useState("");
  const [docType, setDocType] = useState("");
  const [docNum, setDocNum] = useState("");
  const [date, setDate] = useState("");
  const [juris, setJuris] = useState("");
  const [loading, setLoading] = useState(false);
  const [progressMsg, setProgressMsg] = useState("");
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [shareData, setShareData] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef();
  const [pdfLoading, setPdfLoading] = useState(false);

  const handleFile = useCallback((file) => {
    if (!file || file.type !== "application/pdf") { setError("Please select a PDF file."); return; }
    if (file.size > 20 * 1024 * 1024) { setError("PDF too large (max 20 MB). Try a smaller file or paste the text instead."); return; }
    setFileName(file.name); setError(""); setPdfBase64(""); setPdfLoading(true);
    const r = new FileReader();
    r.onload = e => { setPdfBase64(e.target.result.split(",")[1]); setPdfLoading(false); };
    r.onerror = () => { setError("Could not read file."); setPdfLoading(false); };
    r.readAsDataURL(file);
  }, []);

  const runScan = async () => {
    setError(""); setResult(null);
    let messages;
    if (tab === "text") {
      if (!docText.trim()) { setError("Please enter document text."); return; }
      messages = [{ role:"user", content: docText.trim() }];
    } else {
      if (!pdfBase64) { setError("Please upload a PDF."); return; }
      messages = [{ role:"user", content:[{ type:"document", source:{ type:"base64", media_type:"application/pdf", data:pdfBase64 }},{ type:"text", text:"Analyse this corporate disclosure for governance and compliance anomalies." }] }];
    }
    setLoading(true);
    try {
      const meta = [company, docType, docNum, date, juris].filter(Boolean).join(" | ");

      // ── Two-call pipeline ──
      const raw = await runAnalysis({
        messages,
        juris,
        meta,
        onProgress: (msg) => setProgressMsg(msg),
      });

      const parsed = repairJSON(raw);
      if (!parsed) throw new Error('Parse failed. Model returned: ' + raw.slice(0,200));

      // ── Share price layer ──
      let shareData = null;
      if (ticker) {
        setProgressMsg('Fetching share price data…');
        const sd = await fetchShareData(ticker);
        if (sd && parsed.investability_score != null) {
          const lambda = calcLambdaDivergence(parsed.investability_score, sd);
          shareData = { ...sd, lambdaDivergence: lambda?.divergence, lambdaSignal: lambda };
        }
      }

      setResult(parsed);
      setShareData(shareData);
      SESSION_SCANS++;

      // ── Store to Supabase ──
      const entry = buildScanEntry(
        parsed,
        [company, docNum, date].filter(Boolean).join(" · "),
        company, docType, juris, shareData
      );
      storeScan(entry);

    } catch(e) { setError(e.message); }
    finally { setLoading(false); setProgressMsg(''); }
  };

  return (
    <div className="two-col" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", minHeight:"calc(100vh - 64px)" }}>
      <div style={{ padding:"22px", borderRight:"1px solid rgba(180,200,220,0.1)", overflowY:"auto" }}>
        <Lbl>{T.labelLoad}</Lbl>
        <div style={{ display:"flex", border:"1px solid rgba(180,200,220,0.12)", borderRadius:6, overflow:"hidden", marginBottom:12 }}>
          {["text","pdf"].map((tb,i) => <button key={tb} onClick={() => setTab(tb)} style={{ flex:1, padding:"9px 12px", border:"none", borderRight:i===0?"1px solid rgba(180,200,220,0.12)":"none", background:tab===tb?"#3b82f6":"rgba(255,255,255,0.03)", color:tab===tb?"white":"#8899aa", fontFamily:"monospace", fontSize:11, cursor:"pointer" }}>{tb==="text"?T.tabText:T.tabPdf}</button>)}
        </div>

        {tab === "text"
          ? <textarea value={docText} onChange={e=>setDocText(e.target.value)} placeholder={T.placeholderText} style={{ width:"100%", height:170, padding:"11px 13px", border:"1px solid rgba(180,200,220,0.12)", borderRadius:6, background:"rgba(255,255,255,0.04)", fontFamily:"monospace", fontSize:11, lineHeight:1.6, color:"#c8d8e8", resize:"vertical", outline:"none" }}/>
          : <div>
              <div onDragEnter={e=>{e.preventDefault();setDragOver(true)}} onDragOver={e=>{e.preventDefault();setDragOver(true)}} onDragLeave={()=>setDragOver(false)} onDrop={e=>{e.preventDefault();setDragOver(false);handleFile(e.dataTransfer.files[0])}} onClick={()=>fileRef.current?.click()} style={{ border:`2px dashed ${dragOver?"#3b82f6":"rgba(180,200,220,0.2)"}`, borderRadius:6, padding:"28px 18px", textAlign:"center", cursor:"pointer", background:dragOver?"rgba(59,130,246,0.08)":"rgba(255,255,255,0.02)" }}>
                <input ref={fileRef} type="file" accept=".pdf,application/pdf" style={{ display:"none" }} onChange={e=>handleFile(e.target.files[0])}/>
                <div style={{ fontSize:24, marginBottom:9, opacity:0.4 }}>📄</div>
                <div style={{ fontFamily:"monospace", fontSize:11, color:"#8899aa" }}><strong style={{ color:"#c8d8e8", display:"block", marginBottom:3 }}>{T.dropPdf}</strong>{T.dropSub}</div>
              </div>
              {fileName && <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:8, padding:"8px 11px", background: pdfLoading ? "rgba(59,130,246,0.06)" : "rgba(16,185,129,0.06)", border:`1px solid ${pdfLoading ? "rgba(59,130,246,0.2)" : "rgba(16,185,129,0.2)"}`, borderRadius:6, fontFamily:"monospace", fontSize:11, color: pdfLoading ? "#60a5fa" : "#10b981" }}>
                {pdfLoading ? <><Spinner size={11}/> Reading {fileName}…</> : <>✓ {fileName} ready</>}
              </div>}
            </div>
        }

        <div style={{ marginTop:16 }}>
          <Lbl>{T.labelMeta}</Lbl>
          <div className="meta-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:9 }}>
            {[{l:T.labelCompany,v:company,s:setCompany,p:T.placeholderCompany},{l:T.labelDocNum,v:docNum,s:setDocNum,p:T.placeholderNum}].map(f=>(
              <div key={f.l}>
                <div style={{ fontFamily:"monospace", fontSize:9, textTransform:"uppercase", color:"#8899aa", marginBottom:4 }}>{f.l}</div>
                <input value={f.v} onChange={e=>f.s(e.target.value)} placeholder={f.p} style={{ width:"100%", padding:"7px 10px", border:"1px solid rgba(180,200,220,0.12)", borderRadius:4, background:"rgba(255,255,255,0.04)", fontFamily:"monospace", fontSize:12, color:"#c8d8e8", outline:"none" }}/>
              </div>
            ))}
            <div>
              <div style={{ fontFamily:"monospace", fontSize:9, textTransform:"uppercase", color:"#8899aa", marginBottom:4 }}>Ticker symbol (optional)</div>
              <input value={ticker} onChange={e=>setTicker(e.target.value)} placeholder="e.g. AAPL, VOW3.DE, BUR.L" style={{ width:"100%", padding:"7px 10px", border:"1px solid rgba(180,200,220,0.12)", borderRadius:4, background:"rgba(255,255,255,0.04)", fontFamily:"monospace", fontSize:12, color:"#c8d8e8", outline:"none" }}/>
            </div>
            <div>
              <div style={{ fontFamily:"monospace", fontSize:9, textTransform:"uppercase", color:"#8899aa", marginBottom:4 }}>{T.labelDocType}</div>
              <select value={docType} onChange={e=>setDocType(e.target.value)} style={{ width:"100%", padding:"7px 10px", border:"1px solid rgba(180,200,220,0.12)", borderRadius:4, background:"#0d1420", fontFamily:"monospace", fontSize:11, color:"#c8d8e8", outline:"none" }}>
                <option value="">—</option>{T.docTypes.map(a=><option key={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontFamily:"monospace", fontSize:9, textTransform:"uppercase", color:"#8899aa", marginBottom:4 }}>{T.labelDate}</div>
              <input type="date" value={date} onChange={e=>setDate(e.target.value)} style={{ width:"100%", padding:"7px 10px", border:"1px solid rgba(180,200,220,0.12)", borderRadius:4, background:"#0d1420", fontFamily:"monospace", fontSize:11, color:"#c8d8e8", outline:"none" }}/>
            </div>
            <div>
              <div style={{ fontFamily:"monospace", fontSize:9, textTransform:"uppercase", color:"#8899aa", marginBottom:4 }}>{T.labelJuris}</div>
              <select value={juris} onChange={e=>setJuris(e.target.value)} style={{ width:"100%", padding:"7px 10px", border:"1px solid rgba(180,200,220,0.12)", borderRadius:4, background:"#0d1420", fontFamily:"monospace", fontSize:11, color:"#c8d8e8", outline:"none" }}>
                <option value="">—</option>{T.jurisLevels.map(j=><option key={j}>{j}</option>)}
              </select>
            </div>
          </div>
        </div>

        <button onClick={runScan} disabled={loading || pdfLoading || SESSION_SCANS >= BETA_LIMIT} style={{ width:"100%", marginTop:14, padding:"12px", background:(loading||pdfLoading)?"rgba(59,130,246,0.5)":SESSION_SCANS>=BETA_LIMIT?"rgba(180,200,220,0.08)":"#3b82f6", color:SESSION_SCANS>=BETA_LIMIT?"#3b4a5a":"white", border:SESSION_SCANS>=BETA_LIMIT?"1px solid rgba(180,200,220,0.1)":"none", borderRadius:6, fontFamily:"monospace", fontSize:13, fontWeight:700, cursor:(loading||pdfLoading||SESSION_SCANS>=BETA_LIMIT)?"not-allowed":"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:9 }}>
          {(loading||pdfLoading) && <Spinner/>}{loading ? (progressMsg || T.btnScanning) : pdfLoading ? "Reading PDF…" : SESSION_SCANS>=BETA_LIMIT ? "⚖ Book Consultation" : T.btnScan}
        </button>
        {error && <div style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.25)", borderRadius:6, padding:"10px 13px", fontFamily:"monospace", fontSize:11, color:"#ef4444", marginTop:10, lineHeight:1.6, wordBreak:"break-word" }}>⚠ {error}</div>}
        <div style={{ fontFamily:"monospace", fontSize:8, color:"#3b4a5a", marginTop:8, lineHeight:1.7 }}>
          By scanning you agree that anonymised results may be stored for research purposes. No personal data is collected. <span onClick={()=>{}} style={{ color:"#60a5fa", cursor:"pointer" }}>Privacy Policy</span>
        </div>
      </div>

      <div style={{ padding:"22px", overflowY:"auto" }}>
        {!result && !loading && <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:400, gap:16, opacity:0.35 }}><div style={{ fontSize:44 }}>📊</div><div style={{ fontFamily:"monospace", fontSize:11, color:"#8899aa" }}>Paste a document and click Scan</div></div>}
        {loading && !result && <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:400, gap:20 }}>
          <Spinner size={36}/>
          <div style={{ fontFamily:"monospace", fontSize:12, color:"#8899aa" }}>{progressMsg || T.btnScanning}</div>
          <div style={{ fontFamily:"monospace", fontSize:10, color:"#3b4a5a", textAlign:"center", lineHeight:1.8 }}>
            Step 1: Extracting relevant passages<br/>
            Step 2: Calculating legal-physics scores<br/>
            Step 3: Generating anomaly analysis
          </div>
        </div>}
        {result && <ResultsPanel result={result} meta={[company, docNum, date].filter(Boolean).join(" · ")} shareData={shareData} company={company}/>}
      </div>
    </div>
  );
}

// ─── Crawl mode ───────────────────────────────────────────────────────────────
// ─── Research Banner ──────────────────────────────────────────────────────────
function ResearchBanner() {
  const [stats, setStats] = useState(null);

  useState(() => {
    const load = async () => {
      try {
        const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
        const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
        if (!supabaseUrl || !supabaseKey) return;
        const res = await fetch(
          `${supabaseUrl}/rest/v1/scans?select=verdict,juris,entropy,risk`,
          { headers: { 'apikey': supabaseKey, 'Authorization': `Bearer ${supabaseKey}` } }
        );
        const data = await res.json();
        if (!Array.isArray(data)) return;
        const juris = new Set(data.map(s => s.juris).filter(Boolean));
        const avgEntropy = data.length > 0
          ? Math.round(data.reduce((a,s) => a + (s.entropy||0), 0) / data.length)
          : null;
        const highRisk = data.filter(s => (s.risk||0) >= 70).length;
        setStats({ total: data.length, juris: juris.size, avgEntropy, highRisk });
      } catch {}
    };
    load();
  }, []);

  const items = [
    { label:"Scans run", value: stats ? stats.total : "—", color:"#60a5fa" },
    { label:"Avg entropy", value: stats?.avgEntropy != null ? stats.avgEntropy : "—", color:"#ef4444" },
    { label:"High risk flagged", value: stats ? stats.highRisk : "—", color:"#f59e0b" },
  ];

  return (
    <div style={{ margin:"0 0 16px", padding:"12px 16px", background:"rgba(96,165,250,0.04)", border:"1px solid rgba(96,165,250,0.12)", borderRadius:8 }}>
      <div style={{ fontFamily:"monospace", fontSize:8, color:"#60a5fa", textTransform:"uppercase", letterSpacing:"0.15em" }}>
        FIRST EXPERIMENTAL APPLICATION OF S=k·logW AND LEGAL PHYSICS MEASUREMENTS TO CORPORATE DISCLOSURES · Empirical data collection in process — results will be published after a substantial dataset is collected · (See About & Methodology)
      </div>
    </div>
  );
}

function CrawlMode() {
  const [query, setQuery] = useState("");
  const [juris, setJuris] = useState("");
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState([]);
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [entity, setEntity] = useState("");
  const [ticker, setTicker] = useState("");
  const [shareData, setShareData] = useState(null);
  const [maxDocs, setMaxDocs] = useState(3);
  const [progress, setProgress] = useState(null);
  const [pasted, setPasted] = useState(false);
  const abortRef = useRef(false);
  const logEl = useRef(null);

  const addLog = (msg, type="info") => {
    setLog(l => [...l, { msg, type, ts: new Date().toLocaleTimeString() }]);
    setTimeout(() => logEl.current?.scrollTo({ top:99999, behavior:"smooth" }), 50);
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text.trim()) { setQuery(text.trim()); setPasted(true); setTimeout(()=>setPasted(false), 1800); }
    } catch { document.querySelector("#crawl-input")?.focus(); }
  };

  const crawl = async () => {
    if (!query.trim()) return;
    abortRef.current = false;
    setRunning(true); setLog([]); setResults([]); setSelected(null); setEntity(""); setTicker(""); setShareData(null); setProgress(null);

    try {
      // Step 1: verify listing via Yahoo Finance
      addLog(`🔍 Looking up: ${query.trim()}`, "info");
      setProgress({ current:0, total:0, phase:"discovering", currentDoc:"Verifying listing status…" });

      let disc = { entity:null, ticker:null, exchange:null, acts:[] };

      // If user entered a URL, extract domain for Yahoo Finance search
      let searchQuery = query.trim();
      try {
        if (searchQuery.startsWith('http')) {
          const domain = new URL(searchQuery).hostname.replace('www.', '').split('.')[0];
          searchQuery = domain;
          addLog(`🔗 URL detected — searching for: ${domain}`, "info");
        }
      } catch(e) {}

      // Yahoo Finance search to verify listing
      try {
        const yfRes = await fetch(`/api/verify-listing?q=${encodeURIComponent(searchQuery)}`);
        const yfData = await yfRes.json();
        const quotes = yfData?.quotes || [];
        if (quotes.length === 0) {
          addLog("⚠ This company does not appear to be listed on any recognised exchange. The scanner is designed for listed companies with public filings.", "warn");
          setRunning(false); setProgress(null); return;
        }
        const best = quotes[0];
        disc.ticker = best.symbol;
        disc.exchange = best.exchange;
        disc.entity = best.longname || best.shortname || query.trim();
        addLog(`✓ Listed: ${disc.entity} (${disc.ticker} · ${disc.exchange})`, "success");
      } catch(e) {
        addLog(`⚠ Could not verify listing status — proceeding with scan.`, "warn");
        disc.entity = query.trim();
      }

      // Step 2: Claude identifies filings
      setProgress({ current:0, total:0, phase:"discovering", currentDoc:"Identifying recent filings…" });
      try {
        const raw = await callAPI({ model:"claude-sonnet-4-6", system:DISCOVERY_PROMPT, messages:[{ role:"user", content:`Company: ${disc.entity}\nTicker: ${disc.ticker||"unknown"}\nExchange: ${disc.exchange||"unknown"}\n\nIdentify up to 3 real recent public filings for this company.` }] });
        const parsed = repairJSON(raw);
        if (parsed?.acts) disc.acts = parsed.acts;
      } catch(e) { addLog(`Discovery failed: ${e.message}`, "warn"); }

      if (disc.ticker) setTicker(disc.ticker);
      setEntity(disc.entity);

      const allDocs = disc.acts && disc.acts.length > 0 ? disc.acts : 
        disc.ticker ? [{ title:`${disc.entity} — Latest Available Filing`, type:"annual_report", date:new Date().getFullYear()-1+"-12-31", reference:disc.ticker }] : [];
      addLog(`📋 ${disc.acts?.length||0} filings identified${allDocs.length>0&&!disc.acts?.length?" — proceeding with general analysis":""}`, allDocs.length>0?"success":"warn");
      if (allDocs.length === 0) { addLog("💡 No filings found. Try entering the company name or ticker directly (e.g. 'Circle8' or 'CIRC').", "info"); setRunning(false); setProgress(null); return; }

      // Step 2: analyse in parallel
      const limited = allDocs.slice(0, 1); // Beta: 1 filing per scan
      addLog(`⚡ Analysing ${limited.length} filings in parallel…`, "info");
      setProgress({ current:0, total:limited.length, phase:"analysing", currentDoc:`0 / ${limited.length} complete` });
      let done = 0;

      const analyseOne = async (doc, idx) => {
        const lbl = (doc.title || `Filing ${idx+1}`).slice(0, 45);
        const fallback = msg => ({ overall_verdict:"ERROR", risk_score:0, entropy_score:0, inertia_score:0, motive_power:0, summary:msg, anomalies:[], positive_points:[], recommendations:"" });
        let ar;
        try {
          const content = `Analyse this filing for ${disc.entity} for compliance anomalies.\nTitle: ${doc.title||"n/d"}\nType: ${doc.type||"n/d"}\nDate: ${doc.date||"n/d"}\nReference: ${doc.reference||"n/d"}\n\nBased on your knowledge of ${disc.entity} and this filing type, identify realistic governance and compliance risks.`;
          const raw = await callAPI({ model:"claude-sonnet-4-6", system:buildPrompt(juris), messages:[{ role:"user", content }] });
          ar = repairJSON(raw) || fallback("Parse failed.");
        } catch(e) { ar = fallback(e.message); }

        done++;
        const sc = ar.risk_score||0, n=(ar.anomalies||[]).length;
        const ic = sc>=70?"🔴":sc>=40?"🟡":"🟢";
        addLog(ar.overall_verdict==="ERROR" ? `⚠ [${done}/${limited.length}] ${lbl} — ${ar.summary}` : `${ic} [${done}/${limited.length}] ${lbl} — Risk ${sc} · ${n} issue${n!==1?"s":""}`,
          ar.overall_verdict==="ERROR"?"warn":sc>=60?"danger":"success");
        setProgress(p => ({ ...p, current:done, currentDoc:`${done} / ${limited.length} complete` }));
        setResults(prev => { const next=[...prev]; next[idx]={doc,result:ar}; return next; });
        // ── Auto-save to experiment dataset ──
        if (ar.overall_verdict !== "ERROR") {
          const entry = buildScanEntry(ar, doc.title||lbl, disc.entity, doc.type, juris);
          storeScan(entry);
        }
      };

      await Promise.allSettled(limited.map((doc,idx) => analyseOne(doc,idx)));
      addLog(`✅ Done — ${limited.length} filing(s) analysed.`, "success");
      setProgress({ current:limited.length, total:limited.length, phase:"done", currentDoc:"Done" });
      SESSION_SCANS++;
      setSelected(0); // Auto-show the single result

      // ── Auto-fetch share price using discovered ticker ──
      if (disc.ticker) {
        addLog(`📈 Fetching share price for ${disc.ticker}…`, "info");
        try {
          const sd = await fetchShareData(disc.ticker);
          if (sd) {
            addLog(`💹 ${disc.ticker}: ${sd.price ? fmtPrice(sd.price, sd.currency) : "price unavailable"} · Market cap: ${fmtCap(sd.marketCap)}`, "success");
            setShareData(sd);
          }
        } catch { addLog("⚠ Share price fetch failed — continuing without market data.", "warn"); }
      }

    } catch(e) { addLog(`❌ ${e.message}`, "danger"); setProgress(null); }
    setRunning(false);
  };

  const allAnom = results.flatMap(r => r.result?.anomalies || []);
  const avgRisk = results.length > 0 ? Math.round(results.reduce((s,r) => s+(r.result?.risk_score||0),0)/results.length) : 0;
  const critici = results.filter(r => (r.result?.risk_score||0) >= 70).length;

  return (
    <div className="two-col" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", minHeight:"calc(100vh - 64px)" }}>
      <div style={{ padding:"22px", borderRight:"1px solid rgba(180,200,220,0.1)", display:"flex", flexDirection:"column" }}>

        {/* Value proposition */}
        <div style={{ marginBottom:14, padding:"11px 13px", background:"rgba(255,255,255,0.03)", border:"1px solid rgba(180,200,220,0.1)", borderRadius:6, fontSize:11, color:"#94a3b8", lineHeight:1.75 }}>
          Any AI can flag compliance issues in listed companies. Corporate Disclosure Scanner <strong style={{ color:"#c8d8e8" }}>measures them systematically</strong> — to identify, quantify and help address legal inefficiencies that suppress value.
        </div>

        <Lbl>{T.crawlLabel}</Lbl>

        {/* Input row */}
        <div style={{ display:"flex", gap:6, marginBottom:10 }}>
          <div style={{ flex:1, position:"relative", display:"flex", alignItems:"center" }}>
            <input id="crawl-input" value={query} onChange={e=>setQuery(e.target.value)} onKeyDown={e=>e.key==="Enter"&&!running&&crawl()} placeholder={T.crawlPlaceholder}
              style={{ width:"100%", padding:"9px 12px", paddingRight:78, border:"1px solid rgba(180,200,220,0.15)", borderRadius:6, background:"rgba(255,255,255,0.05)", fontFamily:"monospace", fontSize:12, color:"#e8f0f8", outline:"none" }}/>
            <button onClick={handlePaste} style={{ position:"absolute", right:6, top:"50%", transform:"translateY(-50%)", padding:"4px 9px", border:`1px solid ${pasted?"rgba(16,185,129,0.5)":"rgba(180,200,220,0.18)"}`, borderRadius:4, background:pasted?"rgba(16,185,129,0.12)":"rgba(255,255,255,0.06)", color:pasted?"#10b981":"#8899aa", fontFamily:"monospace", fontSize:10, cursor:"pointer", transition:"all 0.25s" }}>
              {pasted?"✓ pasted":"📋 paste"}
            </button>
          </div>
          {running
            ? <button onClick={()=>{ abortRef.current=true; }} style={{ padding:"9px 14px", background:"#c0392b", color:"white", border:"none", borderRadius:6, fontFamily:"monospace", fontSize:11, cursor:"pointer", whiteSpace:"nowrap" }}>{T.crawlStop}</button>
            : SESSION_SCANS >= BETA_LIMIT
            ? <button onClick={requestAccess} style={{ padding:"9px 14px", background:"rgba(59,130,246,0.15)", color:"#60a5fa", border:"1px solid rgba(59,130,246,0.3)", fontWeight:700, borderRadius:6, fontFamily:"monospace", fontSize:11, cursor:"pointer", whiteSpace:"nowrap" }}>⚖ Book Consultation</button>
            : <button onClick={crawl} style={{ padding:"9px 16px", background:"#3b82f6", color:"white", border:"none", borderRadius:6, fontFamily:"monospace", fontSize:11, cursor:"pointer", whiteSpace:"nowrap" }}>{T.crawlBtn}</button>
          }
        </div>

        {/* Jurisdiction + max docs row */}
        <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:10 }}>
          <select value={juris} onChange={e=>setJuris(e.target.value)} style={{ flex:1, padding:"6px 10px", border:"1px solid rgba(180,200,220,0.12)", borderRadius:4, background:"#0d1420", fontFamily:"monospace", fontSize:11, color:juris?"#c8d8e8":"#8899aa", outline:"none" }}>
            <option value="">All jurisdictions</option>{T.jurisLevels.map(j=><option key={j}>{j}</option>)}
          </select>
          <div style={{ display:"flex", alignItems:"center", gap:6, flexShrink:0 }}>
            <span style={{ fontFamily:"monospace", fontSize:10, color:"#8899aa", whiteSpace:"nowrap" }}>Max:</span>
            {[1].map(n => <button key={n} onClick={()=>setMaxDocs(n)} style={{ padding:"4px 9px", border:"1px solid rgba(59,130,246,0.3)", borderRadius:4, background:"rgba(59,130,246,0.18)", color:"#60a5fa", fontFamily:"monospace", fontSize:10, cursor:"default", fontWeight:700 }}>1 <span style={{opacity:0.6, fontSize:8}}>demo</span></button>)}
          </div>
        </div>

        <div style={{ fontFamily:"monospace", fontSize:10, color:"#8899aa", marginBottom:8, lineHeight:1.7, background:"rgba(96,165,250,0.06)", padding:"9px 11px", borderRadius:6, border:"1px solid rgba(96,165,250,0.15)" }}>💡 {T.crawlTip}</div>
        <div style={{ fontFamily:"monospace", fontSize:9, color:"#f59e0b", marginBottom:12, lineHeight:1.7, background:"rgba(245,158,11,0.05)", padding:"8px 11px", borderRadius:6, border:"1px solid rgba(245,158,11,0.2)" }}>
          ⚠ Demo notice: the scanner identifies available filings and analyses <strong>1 randomly selected document</strong>. A full scan covering all filings, the discussion of a comprehensive compliance and / or restructuring programme and the potential transformation of identified liabilities into new investment opportunities with established lawyers are available via consultation. The full version will also show how legal weight, entropy, and investability scores correlate with share price — identifying where the market has not yet priced the governance risk detected in the disclosure.
        </div>

        

        {/* Progress bar */}
        {progress && (
          <div style={{ marginBottom:12, border:"1px solid rgba(180,200,220,0.1)", borderRadius:8, overflow:"hidden", background:"rgba(0,0,0,0.25)" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px 6px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                {progress.phase!=="done" ? <Spinner size={11}/> : <span style={{ color:"#10b981", fontSize:12 }}>✓</span>}
                <span style={{ fontFamily:"monospace", fontSize:10, color:progress.phase==="done"?"#10b981":"#60a5fa", fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase" }}>
                  {progress.phase==="discovering"?"Discovering":progress.phase==="done"?"Complete":`Analysing ${progress.current} / ${progress.total}`}
                </span>
              </div>
              {progress.total>0 && <span style={{ fontFamily:"monospace", fontSize:10, color:"#8899aa" }}>{progress.phase==="done"?"100":Math.round((progress.current/progress.total)*100)}%</span>}
            </div>
            {progress.total>0 && <div style={{ height:3, background:"rgba(180,200,220,0.08)", margin:"0 12px" }}>
              <div style={{ height:"100%", width:progress.phase==="done"?"100%":`${(progress.current/progress.total)*100}%`, background:progress.phase==="done"?"#10b981":"linear-gradient(90deg,#3b82f6,#60a5fa)", borderRadius:2, transition:"width 0.6s ease", boxShadow:progress.phase!=="done"?"0 0 8px rgba(96,165,250,0.6)":"none" }}/>
            </div>}
            {progress.currentDoc && progress.phase!=="done" && <div style={{ padding:"5px 12px 8px", fontFamily:"monospace", fontSize:10, color:"#8899aa" }}>↳ {progress.currentDoc}</div>}
            {progress.total>0 && progress.total<=10 && <div style={{ display:"flex", gap:4, padding:"0 12px 10px", flexWrap:"wrap" }}>
              {Array.from({length:progress.total}).map((_,i) => {
                const done2 = i < progress.current;
                const active = i === progress.current-1 && progress.phase!=="done";
                const sc = results[i]?.result?.risk_score||0;
                return <div key={i} style={{ width:8, height:8, borderRadius:"50%", background:done2?(sc>=70?"#ef4444":sc>=40?"#f59e0b":"#10b981"):"rgba(180,200,220,0.12)", boxShadow:active?"0 0 6px #60a5fa":"none", border:active?"1px solid #60a5fa":"1px solid transparent", transition:"background 0.4s", flexShrink:0 }}/>;
              })}
            </div>}
          </div>
        )}

        {/* Log */}
        {log.length>0 && <>
          <Lbl>{T.logLabel}</Lbl>
          <div ref={logEl} style={{ background:"#050810", borderRadius:6, padding:"11px 13px", height:140, overflowY:"auto", marginBottom:12, fontFamily:"monospace", fontSize:11, lineHeight:1.8, border:"1px solid rgba(180,200,220,0.08)", flexShrink:0 }}>
            {log.map((l,i) => <div key={i} style={{ color:l.type==="danger"?"#ef4444":l.type==="warn"?"#f59e0b":l.type==="success"?"#10b981":"#8899aa", display:"flex", gap:10 }}><span style={{ color:"#3b4a5a", flexShrink:0 }}>{l.ts}</span><span>{l.msg}</span></div>)}
            {running && <div style={{ color:"#8899aa", display:"flex", alignItems:"center", gap:8 }}><Spinner size={11}/> running…</div>}
          </div>
        </>}

        {/* Results list */}
        {results.filter(Boolean).length>0 && <>
          <Lbl>{T.docsLabel} ({results.filter(Boolean).length}{running?"…":""})</Lbl>
          <div style={{ overflowY:"auto", flex:1 }}>
            {results.filter(Boolean).map((r,i) => {
              const sc = r.result?.risk_score||0;
              const dot = sc>=70?"#ef4444":sc>=40?"#f59e0b":"#10b981";
              const n = (r.result?.anomalies||[]).length;
              const sel = selected===i;
              return (
                <div key={i} onClick={()=>setSelected(sel?null:i)} style={{ display:"flex", alignItems:"center", gap:9, padding:"9px 11px", marginBottom:5, border:`1px solid ${sel?"#3b82f6":"rgba(180,200,220,0.1)"}`, borderRadius:6, background:sel?"rgba(59,130,246,0.1)":"rgba(255,255,255,0.02)", cursor:"pointer", transition:"all 0.15s" }}>
                  <div style={{ width:7, height:7, borderRadius:"50%", background:dot, flexShrink:0, boxShadow:`0 0 5px ${dot}88` }}/>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:sel?"#93c5fd":"#e8f0f8", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{r.doc.title||r.doc.url}</div>
                    <div style={{ fontFamily:"monospace", fontSize:9, color:"#8899aa", marginTop:1 }}>{r.doc.type||"filing"}{r.doc.date?` · ${r.doc.date}`:""}</div>
                  </div>
                  <div style={{ textAlign:"right", flexShrink:0 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:dot }}>{sc}</div>
                    <div style={{ fontFamily:"monospace", fontSize:9, color:"#8899aa" }}>{n}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </>}
      </div>

      <div style={{ padding:"22px", overflowY:"auto" }}>
        {selected!==null && results[selected] && results[selected].result
          ? <div>
              <button onClick={()=>setSelected(null)} style={{ fontFamily:"monospace", fontSize:10, padding:"5px 12px", border:"1px solid rgba(180,200,220,0.15)", borderRadius:4, background:"transparent", cursor:"pointer", color:"#8899aa", marginBottom:14 }}>{T.backBtn}</button>
              <ResultsPanel
                result={results[selected].result}
                meta={results[selected].doc?.title||"Filing"}
                company={entity}
                shareData={shareData && results[selected].result?.investability_score != null
                  ? {...shareData, lambdaSignal: calcLambdaDivergence(results[selected].result.investability_score, shareData)}
                  : null}
              />
            </div>
          : results.filter(Boolean).length>0
            ? <div>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:18, paddingBottom:13, borderBottom:"1px solid rgba(180,200,220,0.1)" }}>
                  <div><div style={{ fontSize:15, fontWeight:700, color:"#e8f0f8" }}>{T.summaryLabel}</div><div style={{ fontFamily:"monospace", fontSize:10, color:"#8899aa", marginTop:2 }}>{entity}{ticker?` · ${ticker}`:""} · {results.filter(Boolean).length} filing(s)</div></div>
                  {running && <div style={{ display:"flex", alignItems:"center", gap:7, fontFamily:"monospace", fontSize:11, color:"#8899aa" }}><Spinner size={13}/>analysing…</div>}
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:18 }}>
                  {[{n:results.filter(Boolean).length,l:T.docsLabel,c:"#e8f0f8"},{n:critici,l:T.highRisk,c:"#ef4444"},{n:allAnom.length,l:T.totalAnom,c:"#f59e0b"}].map(s=>(
                    <div key={s.l} style={{ border:"1px solid rgba(180,200,220,0.1)", borderRadius:6, padding:"14px 10px", textAlign:"center", background:"rgba(255,255,255,0.03)" }}>
                      <div style={{ fontSize:24, fontWeight:700, color:s.c }}>{s.n}</div>
                      <div style={{ fontFamily:"monospace", fontSize:8, color:"#8899aa", marginTop:4, textTransform:"uppercase" }}>{s.l}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"72px 1fr", gap:14, marginBottom:18, alignItems:"center" }}>
                  <RiskGauge score={avgRisk} label={T.avgRisk}/>
                  <div>{Object.entries(CAT_META).map(([k,m])=>{const n=allAnom.filter(a=>a.category===k).length;const pct=allAnom.length>0?(n/allAnom.length*100):0;return(<div key={k} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}><span style={{ fontFamily:"monospace", fontSize:9, color:m.color, width:110 }}>{m.icon} {m.label}</span><div style={{ flex:1, height:5, background:"rgba(180,200,220,0.08)", borderRadius:3, overflow:"hidden" }}><div style={{ height:"100%", width:`${pct}%`, background:m.color, borderRadius:3, transition:"width 0.8s ease" }}/></div><span style={{ fontFamily:"monospace", fontSize:10, color:m.color, fontWeight:700, width:16, textAlign:"right" }}>{n}</span></div>);})}</div>
                </div>
                <Lbl>{T.topAnomalies}</Lbl>
                {allAnom.filter(a=>a.severity==="high").length===0
                  ? <div style={{ padding:14, textAlign:"center", fontFamily:"monospace", fontSize:12, color:"#10b981", background:"rgba(16,185,129,0.06)", borderRadius:6, border:"1px solid rgba(16,185,129,0.2)" }}>{T.noAnomalies}</div>
                  : allAnom.filter(a=>a.severity==="high").slice(0,8).map((a,i)=><AnomalyCard key={i} a={a}/>)
                }
                <div style={{ marginTop:12, fontFamily:"monospace", fontSize:10, color:"#8899aa", textAlign:"center" }}>{T.clickHint}</div>
              </div>
            : <div>
                <div style={{ marginBottom:12, padding:"6px 10px", background:"rgba(167,139,250,0.08)", border:"1px solid rgba(167,139,250,0.2)", borderRadius:4, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <span style={{ fontFamily:"monospace", color:"#a78bfa", fontSize:9, textTransform:"uppercase", letterSpacing:"0.1em" }}>⚗ Sample output — fictional company</span>
                  <span style={{ fontFamily:"monospace", color:"#64748b", fontSize:9 }}>Enter a company above to run a real scan</span>
                </div>
                <ResultsPanel
                  result={{
                    overall_verdict:"HIGH RISK",
                    risk_score:74,
                    entropy_score:78,
                    inertia_score:65,
                    motive_power:42,
                    legal_weight_score:81,
                    investability_score:38,
                    pressure_source:"state_pressure",
                    erga_omnes_effect:"Pear Inc.'s undisclosed antitrust investigation and board independence failures create systemic precedent for tech platform regulatory liability across EU and US jurisdictions, amplifying sector-wide compliance risk.",
                    entropy_analysis:"High entropy driven by undisclosed regulatory investigation, opaque board nomination process, and incomplete ESG lifecycle reporting — significant unexpressed liability value.",
                    inertia_analysis:"Governance structures ossified by long-tenured directors and dual-role CEO/chair arrangement, resisting structural evolution despite regulatory pressure.",
                    summary:"Pear Inc.'s annual report presents material disclosure gaps across competition law, governance, and ESG dimensions. The combination of an undisclosed antitrust investigation and board independence failures represents significant unexpressed legal liability not priced by the market.",
                    positive_points:["Strong cash flow disclosure and segment reporting across business units","Regular quarterly filings demonstrate commitment to disclosure cadence","Recent audit committee refreshment improves independence on financial oversight"],
                    recommendations:"Immediately disclose ongoing EU antitrust investigation under IAS 37 and engage specialist competition counsel. Restructure board nomination committee to remove CEO dual role. Develop CSRD-aligned ESG framework with third-party verification ahead of mandatory application.",
                    anomalies:[
                      {
                        severity:"high",
                        category:"COMPETITION LAW",
                        title:"Undisclosed EU Antitrust Investigation",
                        applicable_standards:"TFEU Art.101-102 · EU Damages Directive 2014/104/EU · IAS 37 · MAR Art.17",
                        description:"Annual report does not disclose an ongoing DG COMP investigation into alleged abuse of dominant position in the app distribution market. No provision recorded under IAS 37 despite material probability of liability. Private enforcement exposure through EU Damages Directive is systemic.",
                        legal_analysis:"Failure to disclose a material regulatory investigation violates MAR Art.17 continuous disclosure obligations and IAS 37 contingent liability recognition requirements.",
                        standard_violated:"TFEU Art.102 · IAS 37 · MAR Art.17",
                        doc_section:"Risk Factors / Legal Proceedings",
                        excerpt:"No material legal proceedings are currently pending against the company.",
                        consequence:"Shareholders cannot assess true litigation exposure. Market price likely does not reflect regulatory liability.",
                        erga_omnes:"Sets precedent for mandatory disclosure of DG COMP investigations across EU-listed tech platforms, potentially triggering enhanced regulatory scrutiny sector-wide.",
                        entropy_contribution:85,
                        inertia_contribution:60,
                        norms_violated:["TFEU Art.102","IAS 37 para.14","MAR Art.17","EU Damages Directive 2014/104/EU Art.11"],
                        responsibility:{
                          signatory:{ role:"CEO / CFO", name:null, liability_type:"Civil and regulatory", description:"As signatories of the annual report, CEO and CFO bear primary responsibility for the accuracy and completeness of legal proceedings disclosure under applicable securities law." },
                          internal_controller:{ roles:["Audit Committee","General Counsel","Chief Compliance Officer"], legal_obligation:"IAS 37 · MAR Art.17 · Internal disclosure policy", description:"Audit Committee is required to oversee material contingent liability recognition. General Counsel bears obligation to escalate regulatory investigations to board level for disclosure assessment." },
                          external_oversight:{ bodies:["European Commission DG COMP","ESMA","National Competent Authority"], action:"Investigation · Disclosure enforcement · Potential MAR sanction", deadline:"Ongoing · Next reporting period", description:"DG COMP investigation is ongoing. National competent authority may issue enforcement action for MAR disclosure failure. ESMA guidelines require prompt disclosure of material regulatory proceedings." }
                        }
                      },
                      {
                        severity:"high",
                        category:"GOVERNANCE",
                        title:"Board Independence Gap",
                        applicable_standards:"ESMA Guidelines · NYSE Listing Rules · EU Shareholder Rights Directive II · UK Corporate Governance Code",
                        description:"CEO chairs the board nomination committee. Three of seven directors have prior employment relationships with the company exceeding 5 years. Independence threshold not met under applicable listing rules. Shareholder ability to exercise effective oversight is structurally impaired.",
                        legal_analysis:"Combined CEO/nomination chair role and director tenure concentration violate independence requirements under NYSE listing rules and ESMA corporate governance guidelines.",
                        standard_violated:"NYSE Listed Company Manual § 303A · ESMA Guidelines on Board Independence",
                        doc_section:"Corporate Governance · Board Composition",
                        excerpt:"The Board believes its current composition reflects an appropriate balance of skills and experience.",
                        consequence:"Entrenched board structure reduces accountability and increases risk of self-dealing in executive compensation and strategic decisions.",
                        erga_omnes:"Normalises weak board independence standards across tech sector, reducing investor protection benchmarks for listed technology companies globally.",
                        entropy_contribution:72,
                        inertia_contribution:84,
                        norms_violated:["NYSE Listed Company Manual § 303A.04","ESMA Guidelines on Board Composition","EU Shareholder Rights Directive II Art.9a"],
                        responsibility:{
                          signatory:{ role:"Chairman / CEO", name:null, liability_type:"Fiduciary · Regulatory", description:"As combined Chairman and CEO, the individual bears direct fiduciary responsibility for the governance failures identified and the failure to disclose independence deficiencies accurately." },
                          internal_controller:{ roles:["Nomination Committee","Company Secretary","Institutional Shareholders"], legal_obligation:"NYSE Listing Rules · Fiduciary duty", description:"Nomination Committee, chaired by the CEO, has structural conflict of interest preventing independent board refreshment. Company Secretary bears obligation to advise on compliance with listing rules." },
                          external_oversight:{ bodies:["NYSE Regulation","SEC","Institutional Shareholder Services (ISS)"], action:"Listing compliance review · Proxy advisory recommendation", deadline:"Next AGM", description:"NYSE may review listing compliance. ISS and Glass Lewis likely to recommend against director re-election at next AGM based on independence failures." }
                        }
                      },
                      {
                        severity:"medium",
                        category:"ESG MISREPORTING",
                        title:"Scope 3 Emissions Omission",
                        applicable_standards:"CSRD · GHG Protocol · EU Taxonomy Regulation · TCFD · SFDR",
                        description:"Scope 3 supply chain emissions not reported despite material supply chain footprint. EU Taxonomy alignment claimed at 42% without disclosed methodology or third-party verification. TCFD physical risk assessment absent. CSRD mandatory application imminent.",
                        legal_analysis:"Unverified EU Taxonomy alignment claims without disclosed methodology constitute potential greenwashing under SFDR and EU Taxonomy Regulation.",
                        standard_violated:"EU Taxonomy Regulation Art.8 · CSRD · GHG Protocol Scope 3",
                        doc_section:"Sustainability Report · ESG Data Appendix",
                        excerpt:"The company is committed to achieving net zero by 2040 across its operations.",
                        consequence:"Investors cannot verify ESG claims. Taxonomy misalignment may trigger regulatory challenge and reputational liability.",
                        erga_omnes:"Unverified taxonomy claims normalise greenwashing across the tech sector, undermining SFDR product classification integrity for funds holding tech equities.",
                        entropy_contribution:58,
                        inertia_contribution:45,
                        norms_violated:["EU Taxonomy Regulation Art.8","CSRD ESRS E1","GHG Protocol Scope 3 Standard","TCFD Recommendations"],
                        responsibility:{
                          signatory:{ role:"CFO / Chief Sustainability Officer", name:null, liability_type:"Regulatory · Reputational", description:"CFO and CSO bear joint responsibility for accuracy of ESG disclosures and EU Taxonomy alignment claims in the annual report and sustainability appendix." },
                          internal_controller:{ roles:["Sustainability Committee","External Auditor","ESG Data Team"], legal_obligation:"CSRD · EU Taxonomy Regulation Art.8", description:"Sustainability Committee is required to ensure ESG data accuracy and third-party verification. External auditor bears limited assurance obligation over sustainability disclosures under CSRD." },
                          external_oversight:{ bodies:["European Securities and Markets Authority (ESMA)","National Competent Authority","European Commission"], action:"Greenwashing supervisory action · CSRD compliance review", deadline:"CSRD mandatory application: next reporting cycle", description:"ESMA greenwashing supervisory action is an increasing risk for unverified taxonomy claims. National competent authority may investigate under SFDR if ESG claims affect fund product classifications." }
                        }
                      },
                    ]
                  }}
                  meta="Annual Report 2024"
                  company="Pear Inc."
                  shareData={null}
                />
              </div>
        }
      </div>
    </div>
  );
}

// Storage: scans stored via /api/store-scan (Supabase)

// buildScanEntry imported from ./api

// ─── Dataset / Experiment Dashboard ──────────────────────────────────────────
function DatasetMode() {
  const [scans, setScans]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
      if (!supabaseUrl || !supabaseKey) { setScans([]); setLoading(false); return; }
      const res = await fetch(
        `${supabaseUrl}/rest/v1/scans?select=*&order=ts.desc&limit=500`,
        { headers: { 'apikey': supabaseKey, 'Authorization': `Bearer ${supabaseKey}`, 'Content-Type': 'application/json' } }
      );
      const data = await res.json();
      // normalise column names to camelCase
      setScans((Array.isArray(data) ? data : []).map(s => ({
        ...s,
        docType:      s.doc_type,
        legalWeight:  s.legal_weight,
        pressureSrc:  s.pressure_src,
        anomalyCount: s.anomaly_count,
        highCount:    s.high_count,
        erga_omnes:   s.erga_omnes,
      })));
    } catch { setScans([]); }
    setLoading(false);
  };
  useState(() => { load(); }, []);

  const handleClear = async () => {
    if (!window.confirm("Delete all stored scans? This cannot be undone.")) return;
    setClearing(true);
    try {
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const supabaseKey = process.env.REACT_APP_SUPABASE_SERVICE_KEY || process.env.REACT_APP_SUPABASE_ANON_KEY;
      await fetch(`${supabaseUrl}/rest/v1/scans?id=neq.null`, {
        method: 'DELETE',
        headers: { 'apikey': supabaseKey, 'Authorization': `Bearer ${supabaseKey}` }
      });
    } catch {}
    await load(); setClearing(false);
  };

  const exportJSON = () => {
    const blob = new Blob([JSON.stringify(scans, null, 2)], { type: "application/json" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `cds-dataset-${new Date().toISOString().slice(0,10)}.json`; a.click();
  };

  const exportCSV = () => {
    const cols = ["id","ts","company","docType","juris","verdict","risk","entropy","inertia","motive","legalWeight","investability","pressureSrc","anomalyCount","highCount","categories","erga_omnes"];
    const rows = scans.map(s => cols.map(c => {
      const v = s[c];
      if (Array.isArray(v)) return `"${v.join("; ")}"`;
      if (typeof v === "string" && v.includes(",")) return `"${v}"`;
      return v ?? "";
    }).join(","));
    const csv = [cols.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `cds-dataset-${new Date().toISOString().slice(0,10)}.csv`; a.click();
  };

  // Aggregates
  const n = scans.length;
  const avg = (arr) => arr.length ? Math.round(arr.reduce((a,b)=>a+b,0)/arr.length) : null;
  const nums = (key) => scans.map(s=>s[key]).filter(v=>v!=null);

  const avgRisk    = avg(nums("risk"));
  const avgEntropy = avg(nums("entropy"));
  const avgInertia = avg(nums("inertia"));
  const avgMotive  = avg(nums("motive"));
  const avgWeight  = avg(nums("legalWeight"));
  const avgInvest  = avg(nums("investability"));

  const byVerdict = {};
  const byCat = {};
  const byJuris = {};
  const byDocType = {};
  const byPressure = {};
  scans.forEach(s => {
    byVerdict[s.verdict] = (byVerdict[s.verdict]||0)+1;
    (s.categories||[]).forEach(c => { byCat[c] = (byCat[c]||0)+1; });
    if (s.juris)     byJuris[s.juris]     = (byJuris[s.juris]||0)+1;
    if (s.docType)   byDocType[s.docType] = (byDocType[s.docType]||0)+1;
    if (s.pressureSrc) byPressure[s.pressureSrc] = (byPressure[s.pressureSrc]||0)+1;
  });

  const topCats = Object.entries(byCat).sort((a,b)=>b[1]-a[1]).slice(0,6);
  const verdictColor = v => v?.includes("CRITICAL")?"#ef4444":v?.includes("SIGNIFICANT")?"#f59e0b":v?.includes("MINOR")?"#f59e0b":"#10b981";

  const Stat = ({label, value, color="#e8f0f8", sub}) => (
    <div style={{ border:"1px solid rgba(180,200,220,0.1)", borderRadius:6, padding:"12px 10px", textAlign:"center", background:"rgba(255,255,255,0.03)" }}>
      <div style={{ fontSize:22, fontWeight:700, color, lineHeight:1 }}>{value ?? "—"}</div>
      <div style={{ fontFamily:"monospace", fontSize:8, color:"#8899aa", marginTop:3, textTransform:"uppercase", letterSpacing:"0.05em" }}>{label}</div>
      {sub && <div style={{ fontFamily:"monospace", fontSize:7, color:"#8899aa", marginTop:2, opacity:0.7 }}>{sub}</div>}
    </div>
  );

  const Bar = ({label, value, max, color}) => {
    const pct = max > 0 ? Math.round((value/max)*100) : 0;
    return (
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
        <span style={{ fontFamily:"monospace", fontSize:9, color:"#8899aa", width:140, flexShrink:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{label}</span>
        <div style={{ flex:1, height:5, background:"rgba(180,200,220,0.08)", borderRadius:3, overflow:"hidden" }}>
          <div style={{ height:"100%", width:`${pct}%`, background:color||"#3b82f6", borderRadius:3, transition:"width 0.8s ease" }}/>
        </div>
        <span style={{ fontFamily:"monospace", fontSize:10, color:color||"#3b82f6", fontWeight:700, width:20, textAlign:"right", flexShrink:0 }}>{value}</span>
      </div>
    );
  };

  const ScoreBar = ({label, score, color}) => (
    <div style={{ marginBottom:8 }}>
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
        <span style={{ fontFamily:"monospace", fontSize:9, color:"#8899aa" }}>{label}</span>
        <span style={{ fontFamily:"monospace", fontSize:9, fontWeight:700, color }}>{score!=null?score:"—"}/100</span>
      </div>
      <div style={{ height:5, background:"rgba(180,200,220,0.08)", borderRadius:3 }}>
        {score!=null && <div style={{ height:"100%", width:`${score}%`, background:color, borderRadius:3, transition:"width 1s ease" }}/>}
      </div>
    </div>
  );

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:400, gap:16 }}>
      <Spinner size={28}/><span style={{ fontFamily:"monospace", fontSize:12, color:"#8899aa" }}>Loading dataset…</span>
    </div>
  );

  return (
    <div style={{ padding:"24px", maxWidth:1100, margin:"0 auto" }}>

      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:24 }}>
        <div>
          <div style={{ fontSize:16, fontWeight:700, color:"#e8f0f8", marginBottom:4 }}>🔬 Experiment Dataset</div>
          <div style={{ fontFamily:"monospace", fontSize:10, color:"#8899aa", lineHeight:1.7 }}>
            Every scan is stored here as empirical data · Legal physics framework validation<br/>
            <span style={{ color:"rgba(96,165,250,0.7)", fontSize:9 }}>Physics-reviewed pre-print · Pending empirical publication · This dataset is the experiment</span>
          </div>
        </div>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
          <button onClick={load} style={{ padding:"7px 13px", border:"1px solid rgba(180,200,220,0.15)", borderRadius:5, background:"transparent", color:"#8899aa", fontFamily:"monospace", fontSize:10, cursor:"pointer" }}>↻ Refresh</button>
          <button onClick={exportCSV} disabled={!n} style={{ padding:"7px 13px", border:"1px solid rgba(59,130,246,0.3)", borderRadius:5, background:"rgba(59,130,246,0.08)", color:"#60a5fa", fontFamily:"monospace", fontSize:10, cursor:n?"pointer":"not-allowed", opacity:n?1:0.4 }}>↓ CSV</button>
          <button onClick={exportJSON} disabled={!n} style={{ padding:"7px 13px", border:"1px solid rgba(59,130,246,0.3)", borderRadius:5, background:"rgba(59,130,246,0.08)", color:"#60a5fa", fontFamily:"monospace", fontSize:10, cursor:n?"pointer":"not-allowed", opacity:n?1:0.4 }}>↓ JSON</button>
          <button onClick={handleClear} disabled={!n||clearing} style={{ padding:"7px 13px", border:"1px solid rgba(239,68,68,0.3)", borderRadius:5, background:"rgba(239,68,68,0.06)", color:"#ef4444", fontFamily:"monospace", fontSize:10, cursor:n?"pointer":"not-allowed", opacity:n?1:0.4 }}>🗑 Clear</button>
        </div>
      </div>

      {n === 0 ? (
        <div style={{ textAlign:"center", padding:"80px 40px", opacity:0.4 }}>
          <div style={{ fontSize:48, marginBottom:16 }}>🔬</div>
          <div style={{ fontFamily:"monospace", fontSize:12, color:"#8899aa", lineHeight:2 }}>
            No scans yet.<br/>
            Run analyses in Manual or Scan mode — every result is automatically stored here.
          </div>
        </div>
      ) : (
        <>
          {/* Top stats */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10, marginBottom:24 }}>
            <Stat label="Total scans" value={n} color="#e8f0f8"/>
            <Stat label="Avg risk" value={avgRisk} color={avgRisk>=70?"#ef4444":avgRisk>=40?"#f59e0b":"#10b981"}/>
            <Stat label="Avg entropy" value={avgEntropy} color="#ef4444"/>
            <Stat label="Avg inertia" value={avgInertia} color="#f59e0b"/>
            <Stat label="Avg motive power" value={avgMotive} color="#10b981"/>
          </div>

          {/* Physics scores + distribution */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:16, marginBottom:24 }}>

            {/* Thermodynamic averages */}
            <div style={{ border:"1px solid rgba(180,200,220,0.1)", borderRadius:8, padding:"16px", background:"rgba(255,255,255,0.02)" }}>
              <div style={{ fontFamily:"monospace", fontSize:9, textTransform:"uppercase", color:"#8899aa", marginBottom:12, letterSpacing:"0.1em" }}>⚗ Thermodynamic averages</div>
              <ScoreBar label="Entropy 🔴" score={avgEntropy} color="#ef4444"/>
              <ScoreBar label="Inertia 🟡" score={avgInertia} color="#f59e0b"/>
              <ScoreBar label="Motive Power ⚡" score={avgMotive} color="#10b981"/>
              <ScoreBar label="Legal Weight ⚖" score={avgWeight} color="#7b3fa0"/>
              <ScoreBar label="Investability 🌱" score={avgInvest} color="#3b82f6"/>
            </div>

            {/* Verdict distribution */}
            <div style={{ border:"1px solid rgba(180,200,220,0.1)", borderRadius:8, padding:"16px", background:"rgba(255,255,255,0.02)" }}>
              <div style={{ fontFamily:"monospace", fontSize:9, textTransform:"uppercase", color:"#8899aa", marginBottom:12, letterSpacing:"0.1em" }}>📋 Verdict distribution</div>
              {Object.entries(byVerdict).sort((a,b)=>b[1]-a[1]).map(([v,c]) => (
                <Bar key={v} label={v||"Unknown"} value={c} max={n} color={verdictColor(v)}/>
              ))}
              <div style={{ marginTop:12, borderTop:"1px solid rgba(180,200,220,0.08)", paddingTop:10 }}>
                <div style={{ fontFamily:"monospace", fontSize:9, textTransform:"uppercase", color:"#8899aa", marginBottom:8 }}>Pressure source (PC/PS)</div>
                {Object.entries(byPressure).map(([p,c]) => (
                  <Bar key={p} label={p==="state_pressure"?"🏛 State/PS":p==="client_pressure"?"📈 Market/PC":"✋ Voluntary"} value={c} max={n} color="#60a5fa"/>
                ))}
              </div>
            </div>

            {/* Top anomaly categories */}
            <div style={{ border:"1px solid rgba(180,200,220,0.1)", borderRadius:8, padding:"16px", background:"rgba(255,255,255,0.02)" }}>
              <div style={{ fontFamily:"monospace", fontSize:9, textTransform:"uppercase", color:"#8899aa", marginBottom:12, letterSpacing:"0.1em" }}>⚠ Top anomaly categories</div>
              {topCats.map(([k,c]) => {
                const m = CAT_META[k];
                return <Bar key={k} label={`${m?.icon||""} ${m?.label||k}`} value={c} max={topCats[0]?.[1]||1} color={m?.color||"#8899aa"}/>;
              })}
              <div style={{ marginTop:12, borderTop:"1px solid rgba(180,200,220,0.08)", paddingTop:10 }}>
                <div style={{ fontFamily:"monospace", fontSize:9, textTransform:"uppercase", color:"#8899aa", marginBottom:8 }}>By document type</div>
                {Object.entries(byDocType).sort((a,b)=>b[1]-a[1]).slice(0,4).map(([d,c]) => (
                  <Bar key={d} label={d} value={c} max={n} color="#8899aa"/>
                ))}
              </div>
            </div>
          </div>

          {/* By jurisdiction */}
          {Object.keys(byJuris).length > 0 && (
            <div style={{ border:"1px solid rgba(180,200,220,0.1)", borderRadius:8, padding:"16px", background:"rgba(255,255,255,0.02)", marginBottom:24 }}>
              <div style={{ fontFamily:"monospace", fontSize:9, textTransform:"uppercase", color:"#8899aa", marginBottom:12, letterSpacing:"0.1em" }}>🌐 Scans by jurisdiction</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"4px 24px" }}>
                {Object.entries(byJuris).sort((a,b)=>b[1]-a[1]).map(([j,c]) => (
                  <Bar key={j} label={j} value={c} max={n} color="#3b82f6"/>
                ))}
              </div>
            </div>
          )}

          {/* Recent scans table */}
          <div style={{ border:"1px solid rgba(180,200,220,0.1)", borderRadius:8, overflow:"hidden", background:"rgba(255,255,255,0.02)" }}>
            <div style={{ padding:"12px 16px", borderBottom:"1px solid rgba(180,200,220,0.08)", fontFamily:"monospace", fontSize:9, textTransform:"uppercase", color:"#8899aa", letterSpacing:"0.1em" }}>
              📁 Scan log ({n} entries)
            </div>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontFamily:"monospace", fontSize:10 }}>
                <thead>
                  <tr style={{ background:"rgba(180,200,220,0.04)" }}>
                    {["Date","Company","Type","Jurisdiction","Verdict","Risk","Entropy","Inertia","Motive","Weight","Invest","Anomalies"].map(h => (
                      <th key={h} style={{ padding:"8px 10px", textAlign:"left", color:"#8899aa", fontWeight:600, fontSize:9, textTransform:"uppercase", borderBottom:"1px solid rgba(180,200,220,0.08)", whiteSpace:"nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {scans.slice(0,50).map((s,i) => (
                    <tr key={s.id} style={{ borderBottom:"1px solid rgba(180,200,220,0.06)", background:i%2===0?"transparent":"rgba(180,200,220,0.01)" }}>
                      <td style={{ padding:"7px 10px", color:"#8899aa", whiteSpace:"nowrap" }}>{s.ts.slice(0,10)}</td>
                      <td style={{ padding:"7px 10px", color:"#c8d8e8", maxWidth:140, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.company}</td>
                      <td style={{ padding:"7px 10px", color:"#8899aa", maxWidth:100, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.docType}</td>
                      <td style={{ padding:"7px 10px", color:"#8899aa", maxWidth:100, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.juris}</td>
                      <td style={{ padding:"7px 10px", whiteSpace:"nowrap" }}><span style={{ color:verdictColor(s.verdict), fontWeight:700, fontSize:9 }}>{s.verdict?.split(" ")[0]||"—"}</span></td>
                      {[s.risk, s.entropy, s.inertia, s.motive, s.legalWeight, s.investability].map((v,j) => (
                        <td key={j} style={{ padding:"7px 10px", color: v==null?"#3b4a5a": v>=70?"#ef4444":v>=40?"#f59e0b":"#10b981", fontWeight:700 }}>{v??"-"}</td>
                      ))}
                      <td style={{ padding:"7px 10px", color: s.highCount>0?"#ef4444":"#8899aa" }}>{s.anomalyCount} ({s.highCount}↑)</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {n > 50 && <div style={{ padding:"10px 16px", fontFamily:"monospace", fontSize:9, color:"#8899aa", borderTop:"1px solid rgba(180,200,220,0.08)" }}>Showing 50 of {n} — export CSV/JSON for full dataset</div>}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Error boundary ───────────────────────────────────────────────────────────
class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error:null }; }
  static getDerivedStateFromError(e) { return { error:e }; }
  render() {
    if (this.state.error) return (
      <div style={{ padding:32, fontFamily:"monospace", fontSize:13, color:"#ef4444", background:"rgba(239,68,68,0.06)", margin:24, borderRadius:8, border:"1px solid rgba(239,68,68,0.2)", lineHeight:1.8 }}>
        <strong>Runtime error:</strong><br/>{String(this.state.error)}<br/><br/>
        <button onClick={()=>this.setState({error:null})} style={{ padding:"6px 14px", background:"#3b82f6", color:"white", border:"none", borderRadius:4, cursor:"pointer", fontFamily:"monospace", fontSize:11 }}>Retry</button>
      </div>
    );
    return this.props.children;
  }
}

// ─── App ──────────────────────────────────────────────────────────────────────
function AppInner() {
  const [mode, setMode] = useState("crawl");
  return (
    <div style={{ fontFamily:"'IBM Plex Mono','Courier New',monospace", background:"#080d18", minHeight:"100vh", color:"#e8f0f8" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;600;700&display=swap');
        @keyframes spin{to{transform:rotate(360deg)}}
        *{box-sizing:border-box}
        select,input,textarea{font-family:inherit;color-scheme:dark}
        ::-webkit-scrollbar{width:5px}
        ::-webkit-scrollbar-track{background:rgba(180,200,220,0.04)}
        ::-webkit-scrollbar-thumb{background:rgba(180,200,220,0.15);border-radius:3px}
        input::placeholder,textarea::placeholder{color:#3b4a5a}

        /* ── Mobile responsive ── */
        .two-col { display: grid; grid-template-columns: 1fr 1fr; min-height: calc(100vh - 64px); }
        .nav-bar { display: flex; align-items: center; gap: 12px; }
        .nav-buttons { display: flex; border: 1px solid rgba(180,200,220,0.12); border-radius: 6px; overflow: hidden; }
        .nav-btn { padding: 7px 16px; border: none; border-right: 1px solid rgba(180,200,220,0.12); font-family: monospace; font-size: 11px; cursor: pointer; transition: all 0.15s; white-space: nowrap; }
        .nav-btn:last-child { border-right: none; }
        .header-wrap { padding: 14px 24px; display: flex; justify-content: space-between; align-items: center; }
        .score-grid-4 { display: grid; grid-template-columns: repeat(4,1fr); gap: 7px; margin-bottom: 10px; }
        .score-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px; }
        .cat-grid-4 { display: grid; grid-template-columns: repeat(4,1fr); gap: 5px; margin-bottom: 7px; }
        .thermo-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 8px; }
        .meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 9px; }

        @media (max-width: 768px) {
          .two-col { grid-template-columns: 1fr !important; min-height: unset; }
          .two-col > div:first-child { border-right: none !important; border-bottom: 1px solid rgba(180,200,220,0.1); order: 1; }
          .two-col > div:last-child { order: 2; }
          .nav-bar { flex-wrap: wrap; gap: 6px; }
          .nav-buttons { flex-wrap: wrap; width: 100%; }
          .nav-btn { flex: 1; padding: 8px 8px; font-size: 10px; border-right: none !important; border-bottom: 1px solid rgba(180,200,220,0.12); }
          .nav-btn:last-child { border-bottom: none; }
          .header-wrap { padding: 10px 14px; flex-direction: column; align-items: flex-start; gap: 10px; }
          .score-grid-4 { grid-template-columns: repeat(2,1fr) !important; }
          .score-grid-2 { grid-template-columns: 1fr !important; }
          .cat-grid-4 { grid-template-columns: repeat(2,1fr) !important; }
          .thermo-grid { grid-template-columns: 1fr !important; }
          .meta-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
      <div style={{ position:"fixed", inset:0, backgroundImage:"linear-gradient(rgba(59,130,246,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(59,130,246,0.03) 1px,transparent 1px)", backgroundSize:"40px 40px", pointerEvents:"none", zIndex:0 }}/>
      <div style={{ position:"relative", zIndex:1, borderBottom:"1px solid rgba(180,200,220,0.1)", background:"rgba(8,13,24,0.95)", backdropFilter:"blur(12px)" }} className="header-wrap">
        <div style={{ cursor:"pointer" }} onClick={() => setMode("crawl")}>
          <div style={{ fontSize:16, fontWeight:700, letterSpacing:"0.02em", display:"flex", alignItems:"center", gap:10 }}>
            <span>📊</span>{T.appTitle}
            <span style={{ fontFamily:"monospace", fontSize:9, padding:"2px 6px", borderRadius:3, background:"rgba(59,130,246,0.15)", color:"#60a5fa", border:"1px solid rgba(59,130,246,0.3)", letterSpacing:"0.08em" }}>DEMO</span>
          </div>
          <div style={{ fontFamily:"monospace", fontSize:10, color:"#8899aa", marginTop:3 }}>{T.appSub}</div>
          <div style={{ fontFamily:"monospace", fontSize:8, color:"#ef4444", marginTop:4, letterSpacing:"0.02em", opacity:0.85 }}>
            Governance · Financial Fraud · Market Abuse · ESG Misreporting · Opacity · Related Party · Insider Dealing · Auditor Failure · Competition Law · Data Protection
          </div>
        </div>
        <div className="nav-bar">
          <div className="nav-buttons">
            {[["crawl",T.modeCrawl],["manual",T.modeManual],["methodology","ƒ About & Methodology"],["dataset","🔬 Dataset"]].map(([m,l])=>(
              <button key={m} onClick={()=>setMode(m)} className="nav-btn" style={{ background:mode===m?"rgba(59,130,246,0.2)":"transparent", color:mode===m?"#60a5fa":"#8899aa", display: (m==="manual"||m==="dataset") ? "none" : undefined }}>{l}</button>
            ))}
          </div>
        </div>
      </div>
      <div style={{ position:"relative", zIndex:1 }}>
        {mode==="manual"      ? <ManualMode/>
        : mode==="crawl"      ? <CrawlMode/>
        : mode==="dataset"    ? <DatasetMode/>
        : mode==="methodology"? <Methodology/>
        : mode==="privacy"    ? <Privacy/>
        : <ManualMode/>}
      </div>

      {/* Footer */}
      <div style={{ borderTop:"1px solid rgba(180,200,220,0.08)", padding:"16px 24px", background:"rgba(8,13,24,0.95)", marginTop:40 }}>
        <div style={{ display:"flex", flexWrap:"wrap", justifyContent:"space-between", alignItems:"center", gap:10 }}>
          <div style={{ fontFamily:"monospace", fontSize:9, color:"#3b4a5a", lineHeight:1.8 }}>
            © 2025–2026 Avv. Dr. Gian Marco Solas · All rights reserved<br/>
            <a href="https://ojs.unito.it/index.php/JLMI/article/view/12266" target="_blank" rel="noopener noreferrer" style={{ color:"#60a5fa", textDecoration:"none" }}>JLMI 1/25</a> · <a href="https://papers.ssrn.com/sol3/papers.cfm?abstract_id=5694423" target="_blank" rel="noopener noreferrer" style={{ color:"#60a5fa", textDecoration:"none" }}>"Law is Love" — SSRN 5694423</a>
          </div>
          <div style={{ fontFamily:"monospace", fontSize:9, color:"#3b4a5a", textAlign:"right", lineHeight:1.8 }}>
            Corporate Disclosure Scanner · Demo · Not investment or legal advice<br/>
            <span onClick={()=>setMode("privacy")} style={{ color:"#60a5fa", cursor:"pointer" }}>Privacy Policy &amp; GDPR</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  return <ErrorBoundary><AppInner/></ErrorBoundary>;
}
